import BaseMap from '@/components/BaseMap.vue'
import { LMarker, LPopup, LPolygon, LTooltip, LPolyline, LCircle } from 'vue2-leaflet'
import mapUtils from '@/utils/mapUtils';
import { formatLatitude, formatLongitude } from '@/utils/coordinate';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import bridgeMapStore from '@/store/bridgeMapStore';
import DrawingToolbar from './DrawingToolbar.vue';
import L from 'leaflet'; // 导入Leaflet库
import { createMapLabelManager } from '@/utils/mapLabelManager'; // 文本标注防重叠管理器

export default {
  name: 'BridgeMap',
  components: {
    BaseMap,
    LMarker,
    LPopup,
    LPolygon,
    LTooltip,
    LPolyline,
    LCircle,
    DrawingToolbar
  },
  props: {
    // 桥梁数据
    bridgeMarkers: {
      type: Array,
      default: () => []
    },
    // 当前桥梁信息（用于定位对话框显示桥梁位置）
    currentBridgeInfo: {
      type: Object,
      default: null
    },
    // 是否为编辑模式
    isEdit: {
      type: Boolean,
      default: false
    },
    // 当前桥梁坐标
    marker: {
      type: Array,
      default: null
    },
    // 是否有桥梁坐标
    hasCoordinates: {
      type: Boolean,
      default: false
    },
    // 区域数据
    savedAreas: {
      type: Array,
      default: () => []
    },
    // 区域类型名称映射
    areaTypeNameMap: {
      type: Object,
      default: () => ({})
    },
    // 当前正在绘制的坐标
    drawingCoordinates: {
      type: Array,
      default: () => []
    },
    // 是否处于绘制模式
    drawingMode: {
      type: Boolean,
      default: false
    },
    // 当前区域类型
    currentAreaType: {
      type: String,
      default: ''
    },
    // 🎯 当前几何类型（从父组件传入）
    currentGeometryTypeProp: {
      type: String,
      default: 'POLYGON'
    }
  },
  data() {
    return {
      zoom: 12,
      center: [23.13, 113.27], // 广州的坐标
      mapOptions: {
        zoomControl: false,
        attributionControl: false
      },
      // 鼠标位置坐标
      mouseLat: 0,
      mouseLng: 0,
      // 当前鼠标位置（用于绘制预览线段）
      currentMousePosition: null,
      // 是否禁用自动闭合
      disableAutoClosing: true, // 默认禁用自动闭合效果
      // 是否显示填充多边形
      shouldShowFilledPolygon: false,
      // 绘制是否已完成（完成后允许编辑点）
      drawingCompleted: false,
      // 地图实例
      map: null,
      // 引用状态存储
      mapStore: bridgeMapStore.state,
      // 绘制历史记录，用于实现撤销/重做
      drawingHistory: [],
      // 图层管理面板显示状态
      layerManagerVisible: false,
      // 当前地图类型
      currentMapType: 'satellite', // 默认使用天地图
      // 当前绘制图形的颜色
      currentDrawingColor: null,
      // 右键菜单状态
      contextMenuVisible: false,
      contextMenuPosition: {
        top: 0,
        left: 0
      },
      // 当前选中的点索引（用于右键菜单删除点）
      selectedPointIndex: null,
      // 拖拽相关状态
      dragStartPoint: null,
      draggingPointIndex: null,
      // 标记地图拖拽是否被临时禁用
      mapDraggingDisabled: false,
      // 标记是否正在拖拽点
      isPointDragging: false,
      // 拖拽点的当前临时位置（用于预览）
      dragPreviewPosition: null,
      // 标记是否按下鼠标（mousedown状态）
      isMouseDown: false,
      // 记录与拖拽点相连的前后点索引
      connectedPointsBefore: null, // 拖拽点前一个点的索引
      connectedPointsAfter: null,  // 拖拽点后一个点的索引
      
      // 点标注相关
      currentPointIcon: 'el-icon-location', // 当前点标注使用的图标
      pointMarkers: [], // 点标注标记列表
      
      // Leaflet对象
      L: L,
      
      // 地图状态更新计数器，用于触发计算属性重新计算
      mapUpdateCounter: 0,
      
      // 实时更新节流控制
      realtimeUpdateTimer: null,
      
      // 🎯 矩形拖拽预览坐标
      rectangleDragPreview: null,
      
      // 🎯 拖拽状态日志标识
      _loggedDragState: false,
      
      // 🎯 测距工具相关（基于线段标注重新实现）
      isMeasureMode: false,
      measureLines: [], // 存储测距线段
      measureDrawing: false, // 是否正在绘制测距线段
      measureCoordinates: [], // 当前绘制的测距线段坐标
      measureMousePosition: null, // 测距预览鼠标位置
      measureStartMarker: null, // 起点标记的Leaflet对象
      measurePreviewPolyline: null, // 预览线段的Leaflet对象
      measurePreviewTooltip: null // 预览提示的Leaflet对象
      

    };
  },
  
  computed: {
    // 统一的点标注数据 - 合并已保存和正在绘制的点标注
    allPointMarkers() {
      const markers = [];
      
      // 1. 添加已保存的点标注
      if (this.savedAreas && this.savedAreas.length > 0) {
        this.savedAreas.forEach((area, index) => {
          if (area.geometryType === 'POINT' && 
              mapUtils.parseAreaCoordinates(area) && 
              mapUtils.parseAreaCoordinates(area).length >= 1 && 
              area.visible !== false) {
            
            const coordinates = mapUtils.parseAreaCoordinates(area)[0];
            
            markers.push({
              id: area.id || `saved_${index}`,
              type: 'saved',
              position: coordinates,
              label: area.areaName || area.name || '未命名区域',
              color: area.color || '#409EFF',
              iconName: area.icon || 'el-icon-location',
              cssClass: 'custom-point-marker saved-point-marker',
              iconSize: [36, 36],
              // 非编辑模式iconAnchor测试值 - 请测试以下选项：
              iconAnchor: [18, 18], // 选项1：忽略border，标准中心
              // iconAnchor: [20, 20], // 选项2：考虑border，理论中心  
              // iconAnchor: [18, 30], // 选项3：X轴标准，Y轴偏移
              // iconAnchor: [20, 30], // 选项4：X轴考虑border，Y轴偏移（类似编辑模式的Y偏移）
              visible: area.visible,
              originalData: area // 保存原始数据用于点击事件
            });
          }
        });
      }
      
      // 2. 添加正在绘制的点标注
      if (this.pointMarkers && this.pointMarkers.length > 0) {
        this.pointMarkers.forEach((marker, index) => {
          if (marker.position && 
              marker.position.length === 2 && 
              marker.visible !== false) {
            
            markers.push({
              id: marker.id || `drawing_${index}`,
              type: 'drawing',
              position: marker.position,
              label: marker.label,
              color: marker.color,
              iconName: marker.iconName,
              cssClass: 'custom-point-marker',
              iconSize: [36, 36],
              // iconAnchor: [18, 18], // 编辑模式
              iconAnchor: [18, 36], // 编辑模式
              // iconAnchor: [9999, 9999], // 编辑模式
              visible: marker.visible,
              originalData: marker // 保存原始数据用于点击事件
            });
          }
        });
      }
      
      return markers;
    },

    // 点标注icon渲染数据 - 使用自定义位置计算
    pointMarkerIcons() {
      // 依赖mapUpdateCounter确保地图状态变化时重新计算
      // eslint-disable-next-line no-unused-vars
      const updateTrigger = this.mapUpdateCounter;
      
      if (!this.map) return [];
      
      const icons = [];
      
      // 处理已保存的点标注
      if (this.savedAreas && this.savedAreas.length > 0) {
        this.savedAreas.forEach((area, index) => {
          // 获取当前选中的区域
          const currentArea = bridgeMapStore.state.currentArea;
          const isCurrentEditingArea = currentArea && currentArea.id === area.id;
          
          // 对于点标注类型，如果是当前正在编辑的区域，或者满足常规显示条件，则显示
          if (area.geometryType === 'POINT' && 
              mapUtils.parseAreaCoordinates(area) && 
              mapUtils.parseAreaCoordinates(area).length >= 1 && 
              (area.visible !== false || isCurrentEditingArea)) {
            
            const coordinates = mapUtils.parseAreaCoordinates(area)[0];
            const marker = {
              id: area.id || `saved_icon_${index}`,
              type: 'saved',
              position: coordinates,
              label: area.areaName || area.name || '未命名区域',
              color: area.color || '#409EFF',
              iconName: area.icon || 'el-icon-location',
              iconSize: [36, 36],
              originalData: area
            };
            
            // 计算样式
            const style = this.getPointMarkerIconStyle(marker);
            if (style.display !== 'none') {
              // 检测是否正在被拖拽（编辑模式下可能被拖拽）
              const isDragging = isCurrentEditingArea && this.isPointDragging && this.draggingPointIndex === 0;
              const cssClasses = ['custom-point-marker-icon'];
              if (isDragging) cssClasses.push('dragging');
              if (isCurrentEditingArea && this.drawingCompleted) cssClasses.push('editing');
              
              icons.push({
                ...marker,
                style: style,
                cssClasses: cssClasses.join(' ')
              });
            }
          }
        });
      }
      
      // 处理正在绘制的点标注
      if (this.pointMarkers && this.pointMarkers.length > 0) {
        this.pointMarkers.forEach((marker, index) => {
          if (marker.position && 
              marker.position.length === 2 && 
              marker.visible !== false) {
            
            const iconMarker = {
              id: marker.id || `drawing_icon_${index}`,
              type: 'drawing',
              position: marker.position,
              label: marker.label,
              color: marker.color,
              iconName: marker.iconName,
              iconSize: [36, 36],
              originalData: marker
            };
            
            // 计算样式
            const style = this.getPointMarkerIconStyle(iconMarker);
            if (style.display !== 'none') {
              // 检测是否正在被拖拽（正在绘制的点标注在编辑模式下可能被拖拽）
              const isDragging = this.isPointDragging && this.draggingPointIndex === 0;
              const cssClasses = ['custom-point-marker-icon'];
              if (isDragging) cssClasses.push('dragging');
              if (this.drawingCompleted) cssClasses.push('editing');
              
              icons.push({
                ...iconMarker,
                style: style,
                cssClasses: cssClasses.join(' ')
              });
            }
          }
        });
      }
      
      // 特殊处理：如果当前正在编辑点标注，但该点标注已从savedAreas中移除（避免重叠显示）
      // 我们需要确保编辑中的点标注icon仍然显示
      const currentArea = bridgeMapStore.state.currentArea;
      if (currentArea && 
          currentArea.geometryType === 'POINT' && 
          this.drawingCompleted && 
          this.drawingCoordinates && 
          this.drawingCoordinates.length === 1) {
        
        // 检查当前编辑的点标注是否已经在icons中了
        const existingIconIndex = icons.findIndex(icon => 
          icon.originalData && icon.originalData.id === currentArea.id
        );
        
        if (existingIconIndex === -1) {
          // 如果没有找到，说明当前编辑的点标注因为从savedAreas中移除而没有显示
          // 我们需要为它单独创建一个icon
          
          // 【修复区域名称缺失】：确保originalData包含完整的区域信息
          let completeAreaData = { ...currentArea };
          
          // 如果currentArea缺少名称字段，尝试从保存的props中恢复
          if (!completeAreaData.areaName && !completeAreaData.name) {
            // 尝试从savedAreas的历史记录中查找完整数据
            const originalArea = this.savedAreas?.find(area => area.id === currentArea.id);
            if (originalArea) {
              console.log('🔧 从savedAreas恢复缺失的区域名称:', originalArea.areaName || originalArea.name);
              completeAreaData.areaName = originalArea.areaName;
              completeAreaData.name = originalArea.name;
            } else {
              // 如果找不到原始数据，提供默认名称
              completeAreaData.areaName = '点标注';
              completeAreaData.name = '点标注';
              console.warn('⚠️  无法恢复区域名称，使用默认名称');
            }
          }
          
          const marker = {
            id: currentArea.id || 'current_editing',
            type: 'editing',
            position: this.drawingCoordinates[0],
            label: completeAreaData.areaName || completeAreaData.name || '未命名区域',
            color: currentArea.color || '#409EFF',
            iconName: currentArea.icon || 'el-icon-location',
            iconSize: [36, 36],
            originalData: completeAreaData  // 使用完整的数据
          };
          
          // 计算样式
          const style = this.getPointMarkerIconStyle(marker);
          if (style.display !== 'none') {
            const isDragging = this.isPointDragging && this.draggingPointIndex === 0;
            const cssClasses = ['custom-point-marker-icon'];
            if (isDragging) cssClasses.push('dragging');
            cssClasses.push('editing');
            
            icons.push({
              ...marker,
              style: style,
              cssClasses: cssClasses.join(' ')
            });
          }
        }
      }

      return icons;
    },

    // 判断当前是否在编辑已保存的点标注
    isEditingSavedPointMarker() {
      // 检查是否在编辑模式且绘制坐标只有一个点
      if (!this.drawingCompleted || !this.drawingCoordinates || this.drawingCoordinates.length !== 1) {
        return false;
      }
      
      // 检查当前选中的区域是否为点标注类型
      const currentArea = bridgeMapStore.state.currentArea;
      if (currentArea && currentArea.geometryType === 'POINT') {
        return true;
      }
      
      // 如果没有正在绘制的点标注，但有绘制坐标，可能是在编辑已保存的点标注
      return this.pointMarkers.length === 0;
    },
    
    // 判断当前是否为点标注模式
    isPointMarkerMode() {
      return this.pointMarkers.length > 0 && this.drawingCoordinates.length === 1;
    },
    
    // 判断当前几何类型
    currentGeometryType() {
      // 🎯 优先使用从父组件传入的几何类型
      if (this.currentGeometryTypeProp && this.currentGeometryTypeProp !== 'POLYGON') {
        return this.currentGeometryTypeProp;
      }
      
      // 从store中获取标记类型
      if (bridgeMapStore.state.markType === 'line') return 'LINESTRING';
      if (bridgeMapStore.state.markType === 'rectangle') return 'RECTANGLE';
      if (bridgeMapStore.state.markType === 'circle') return 'CIRCLE';
      if (bridgeMapStore.state.markType === 'point') return 'POINT';
      
      // 优先从currentAreaType中获取
      if (this.currentAreaType === 'LINESTRING') return 'LINESTRING';
      if (this.currentAreaType === 'RECTANGLE') return 'RECTANGLE';
      if (this.currentAreaType === 'CIRCLE') return 'CIRCLE';
      
      // 最后使用传入的prop或默认值
      return this.currentGeometryTypeProp || 'POLYGON';
    },
    
    // 判断是否为线段类型
    isLineStringType() {
      return this.currentGeometryType === 'LINESTRING';
    },
    
    // 判断是否为矩形类型  
    isRectangleType() {
      return this.currentGeometryType === 'RECTANGLE';
    },
    
    // 判断是否为圆形类型
    isCircleType() {
      return this.currentGeometryType === 'CIRCLE';
    },
    
    // 获取当前类型允许的最大点数
    maxAllowedPoints() {
      switch (this.currentGeometryType) {
        case 'LINESTRING': return 2;
        case 'RECTANGLE': return 4;
        case 'CIRCLE': return 2; // 圆形需要2个点：中心点+边界点
        default: return Infinity; // 多边形无限制
      }
    },
    
    // 🎯 判断是否可以完成绘制
    canFinishDrawing() {
      if (!this.drawingCoordinates || this.drawingCompleted) return false;
      
      switch (this.currentGeometryType) {
        case 'LINESTRING':
          return this.drawingCoordinates.length >= 2;
        case 'RECTANGLE':
          return this.drawingCoordinates.length >= 4;
        case 'CIRCLE':
          return this.drawingCoordinates.length >= 2;
        default: // POLYGON
          return this.drawingCoordinates.length >= 3;
      }
    },
    
    // 🎯 判断是否可以删除点
    canDeletePoint() {
      if (!this.drawingCompleted || !this.drawingCoordinates) return false;
      
      switch (this.currentGeometryType) {
        case 'LINESTRING':
          return false; // 线段不允许删除点
        case 'RECTANGLE':
          return false; // 矩形不允许删除点
        case 'CIRCLE':
          return false; // 圆形不允许删除点
        default: // POLYGON
          return this.drawingCoordinates.length > 3; // 多边形至少保留3个点
      }
    },
    
    // 🎯 判断是否可以添加新点
          canAddNewPoint() {
        if (!this.drawingCompleted || !this.drawingCoordinates) return false;
        
        switch (this.currentGeometryType) {
          case 'LINESTRING':
            return false; // 线段不允许添加点
          case 'RECTANGLE':
            return false; // 矩形不允许添加点
          case 'CIRCLE':
            return false; // 圆形不允许添加点
          default: // POLYGON
            return true; // 多边形允许添加点
        }
      },
      
      // 🎯 判断是否正在拖拽矩形
      isRectangleDragging() {
        const result = this.isRectangleType && this.isPointDragging && this.selectedPointIndex !== null;
        if (result && !this._loggedDragState) {
          console.log('🟢 矩形拖拽模式已启用');
          this._loggedDragState = true;
        } else if (!result && this._loggedDragState) {
          console.log('🔴 矩形拖拽模式已停用');
          this._loggedDragState = false;
        }
        return result;
      },
      

  },
  
  created() {
    // 注册事件监听
    this.setupEventListeners();
    
    // 🎯 监听绘制完成事件
    BridgeEventBus.$on(BRIDGE_EVENTS.DRAWING_ENDED, this.handleDrawingEnded);
    
    // 添加全局点击事件监听，用于关闭右键菜单
    document.addEventListener('click', this.hideContextMenu);
    
    // 添加全局mousemove监听，用于跟踪拖拽中的鼠标位置
    document.addEventListener('mousemove', this.handleGlobalMouseMove);
    
    // 添加全局mouseup监听，用于处理鼠标释放
    document.addEventListener('mouseup', this.handleGlobalMouseUp);
  },
  
  beforeDestroy() {
    // 注销事件监听
    this.removeEventListeners();
    
    // 🎯 取消监听绘制完成事件
    BridgeEventBus.$off(BRIDGE_EVENTS.DRAWING_ENDED, this.handleDrawingEnded);
    
    // 移除全局点击事件监听
    document.removeEventListener('click', this.hideContextMenu);
    
    // 移除全局mousemove监听
    document.removeEventListener('mousemove', this.handleGlobalMouseMove);
    
    // 移除全局mouseup监听
    document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    
    // 清除实时更新定时器
    if (this.realtimeUpdateTimer) {
      clearTimeout(this.realtimeUpdateTimer);
      this.realtimeUpdateTimer = null;
    }
    
    // 确保在组件销毁前恢复地图拖拽功能
    this.restoreMapDragging();
  },
  
  methods: {
    // 🎯 获取可编辑的点（矩形只显示前4个点的拖拽点，圆形显示2个点）
    getEditablePoints() {
      if (!this.drawingCoordinates || this.drawingCoordinates.length === 0) {
        return [];
      }
      
      if (this.isRectangleType && this.drawingCoordinates.length === 5) {
        // 矩形有5个点时，只返回前4个可编辑点（排除闭合点）
        return this.drawingCoordinates.slice(0, 4);
      }
      
      if (this.isCircleType && this.drawingCoordinates.length >= 2) {
        // 圆形只显示中心点和边界点（前2个点）
        return this.drawingCoordinates.slice(0, 2);
      }
      
      // 其他情况返回所有点
      return this.drawingCoordinates;
    },
    
    // 🎯 计算矩形拖拽时的限制位置
    calculateRectangleDragPosition(dragPoint, dragIndex) {
      if (!this.isRectangleType || !this.drawingCoordinates || this.drawingCoordinates.length !== 4) {
        return dragPoint;
      }
      
      try {
        // 矩形的4个点按顺序：左上、右上、右下、左下
        const points = [...this.drawingCoordinates];
        
        // 根据拖拽的点索引来限制移动
        switch (dragIndex) {
          case 0: // 左上角
            return [dragPoint[0], points[1][1]]; // 只能垂直移动
          case 1: // 右上角  
            return [points[0][0], dragPoint[1]]; // 只能水平移动
          case 2: // 右下角
            return [dragPoint[0], points[3][1]]; // 只能垂直移动
          case 3: // 左下角
            return [points[2][0], dragPoint[1]]; // 只能水平移动
          default:
            return dragPoint;
        }
      } catch (error) {
        console.warn('计算矩形拖拽位置出错:', error);
        return dragPoint;
      }
    },
    
    // 🎯 更新整个矩形的坐标（保持直角）
    updateRectangleCoordinates(dragIndex, newPosition) {
      if (!this.isRectangleType || !this.drawingCoordinates || this.drawingCoordinates.length !== 4) {
        return this.drawingCoordinates;
      }
      
      try {
        // 重新计算整个矩形，确保所有边都保持直角
        const points = [...this.drawingCoordinates];
        
        // 根据拖拽的顶点重新计算矩形的所有顶点
        switch (dragIndex) {
          case 0: // 拖拽左上角 [topLeft, topRight, bottomRight, bottomLeft]
            points[0] = newPosition;                         // 左上角
            points[1] = [points[2][0], newPosition[1]];      // 右上角：使用右下角的X，新位置的Y
            points[3] = [newPosition[0], points[2][1]];      // 左下角：使用新位置的X，右下角的Y
            // points[2] 右下角保持不变
            break;
          case 1: // 拖拽右上角
            points[1] = newPosition;                         // 右上角
            points[0] = [points[3][0], newPosition[1]];      // 左上角：使用左下角的X，新位置的Y
            points[2] = [newPosition[0], points[3][1]];      // 右下角：使用新位置的X，左下角的Y
            // points[3] 左下角保持不变
            break;
          case 2: // 拖拽右下角
            points[2] = newPosition;                         // 右下角
            points[1] = [newPosition[0], points[0][1]];      // 右上角：使用新位置的X，左上角的Y
            points[3] = [points[0][0], newPosition[1]];      // 左下角：使用左上角的X，新位置的Y
            // points[0] 左上角保持不变
            break;
          case 3: // 拖拽左下角
            points[3] = newPosition;                         // 左下角
            points[0] = [newPosition[0], points[1][1]];      // 左上角：使用新位置的X，右上角的Y
            points[2] = [points[1][0], newPosition[1]];      // 右下角：使用右上角的X，新位置的Y
            // points[1] 右上角保持不变
            break;
        }
        
        console.log(`矩形顶点${dragIndex}拖拽更新，新坐标:`, points);
        return points;
      } catch (error) {
        console.warn('更新矩形坐标出错:', error);
        return this.drawingCoordinates;
      }
    },
    
    // 🎯 获取矩形预览的坐标（用于第一个点后的预览效果）
    getRectanglePreviewCoordinates() {
      if (!this.isRectangleType || 
          !this.drawingCoordinates || 
          this.drawingCoordinates.length !== 1 || 
          !this.currentMousePosition) {
        return [];
      }
      
      try {
        const firstPoint = this.drawingCoordinates[0];
        const mousePoint = this.currentMousePosition;
        
        // 计算矩形的四个角点
        const topLeft = firstPoint;
        const topRight = [firstPoint[0], mousePoint[1]];
        const bottomRight = mousePoint;
        const bottomLeft = [mousePoint[0], firstPoint[1]];
        
        // 返回矩形的四个顶点（闭合）
        return [topLeft, topRight, bottomRight, bottomLeft, topLeft];
      } catch (error) {
        console.warn('计算矩形预览坐标出错:', error);
        return [];
      }
    },
    
    // 🎯 获取圆形预览的坐标（用于第一个点后的预览效果）
    getCirclePreviewCoordinates() {
      if (!this.isCircleType || 
          !this.drawingCoordinates || 
          this.drawingCoordinates.length !== 1 || 
          !this.currentMousePosition) {
        return [];
      }
      
      try {
        const centerPoint = this.drawingCoordinates[0];
        const mousePoint = this.currentMousePosition;
        
        // 计算半径（使用经纬度距离计算）
        const radius = this.calculateDistance(centerPoint, mousePoint);
        
        // 生成圆形的近似多边形坐标
        return this.generateCirclePolygon(centerPoint, radius);
      } catch (error) {
        console.warn('计算圆形预览坐标出错:', error);
        return [];
      }
    },
    
    // 🎯 获取矩形显示坐标（确保正确闭合显示）
    getRectangleDisplayCoordinates() {
      if (!this.isRectangleType || !this.drawingCoordinates || this.drawingCoordinates.length < 4) {
        return [];
      }
      
      try {
        // 确保矩形正确闭合：添加第一个点作为最后一个点
        const coords = [...this.drawingCoordinates];
        if (coords.length === 4) {
          coords.push(coords[0]); // 闭合矩形
        }
        return coords;
      } catch (error) {
        console.warn('获取矩形显示坐标出错:', error);
        return this.drawingCoordinates;
      }
    },
    
    // 🎯 获取圆形中心点
    getCircleCenter() {
      if (!this.isCircleType || !this.drawingCoordinates || this.drawingCoordinates.length < 1) {
        return [0, 0];
      }
      return this.drawingCoordinates[0];
    },
    
    // 🎯 获取圆形半径（单位：米）
    getCircleRadiusInMeters() {
      if (!this.isCircleType || !this.drawingCoordinates || this.drawingCoordinates.length < 2) {
        return 0;
      }
      
      try {
        const centerPoint = this.drawingCoordinates[0];
        const edgePoint = this.drawingCoordinates[1];
        
        // 使用Haversine公式计算真实距离（米）
        return this.calculateDistanceInMeters(centerPoint, edgePoint);
      } catch (error) {
        console.warn('计算圆形半径出错:', error);
        return 0;
      }
    },
    
    // 🎯 获取圆形预览半径（单位：米）
    getCirclePreviewRadiusInMeters() {
      if (!this.isCircleType || !this.drawingCoordinates || this.drawingCoordinates.length < 1 || !this.currentMousePosition) {
        return 0;
      }
      
      try {
        const centerPoint = this.drawingCoordinates[0];
        const mousePoint = this.currentMousePosition;
        
        // 使用Haversine公式计算真实距离（米）
        return this.calculateDistanceInMeters(centerPoint, mousePoint);
      } catch (error) {
        console.warn('计算圆形预览半径出错:', error);
        return 0;
      }
    },
    
    // 🎯 获取已保存圆形区域的半径（单位：米）
    getSavedAreaCircleRadius(area) {
      if (!area || area.geometryType !== 'CIRCLE') {
        return 0;
      }
      
      try {
        const coordinates = mapUtils.parseAreaCoordinates(area);
        if (!coordinates || coordinates.length < 2) {
          return 0;
        }
        
        const centerPoint = coordinates[0];
        const edgePoint = coordinates[1];
        
        // 使用Haversine公式计算真实距离（米）
        return this.calculateDistanceInMeters(centerPoint, edgePoint);
      } catch (error) {
        console.warn('计算已保存圆形区域半径出错:', error);
        return 0;
      }
    },
    
    // 🎯 获取圆形拖拽预览的中心点
    getCircleDragPreviewCenter() {
      if (!this.isCircleType || !this.drawingCoordinates || this.drawingCoordinates.length < 2 || !this.isPointDragging) {
        return [0, 0];
      }
      
      try {
        if (this.draggingPointIndex === 0) {
          // 拖拽中心点：使用拖拽位置作为新中心点
          return this.dragPreviewPosition || this.drawingCoordinates[0];
        } else {
          // 拖拽边界点：中心点不变
          return this.drawingCoordinates[0];
        }
      } catch (error) {
        console.warn('获取圆形拖拽预览中心点出错:', error);
        return this.drawingCoordinates[0];
      }
    },
    
    // 🎯 获取圆形拖拽预览的半径（单位：米）
    getCircleDragPreviewRadius() {
      if (!this.isCircleType || !this.drawingCoordinates || this.drawingCoordinates.length < 2 || !this.isPointDragging || !this.dragPreviewPosition) {
        return 0;
      }
      
      try {
        if (this.draggingPointIndex === 0) {
          // 🎯 拖拽中心点：半径保持不变（使用原始半径）
          const originalCenterPoint = this.drawingCoordinates[0];
          const originalEdgePoint = this.drawingCoordinates[1];
          return this.calculateDistanceInMeters(originalCenterPoint, originalEdgePoint);
        } else {
          // 拖拽边界点：计算原中心点到新边界点的距离
          const centerPoint = this.drawingCoordinates[0];
          const newEdgePoint = this.dragPreviewPosition;
          return this.calculateDistanceInMeters(centerPoint, newEdgePoint);
        }
      } catch (error) {
        console.warn('计算圆形拖拽预览半径出错:', error);
        return 0;
      }
    },
    
    // 🎯 获取圆形拖拽预览的半径线段坐标
    getCircleDragPreviewRadiusLine() {
      if (!this.isCircleType || !this.drawingCoordinates || this.drawingCoordinates.length < 2 || !this.isPointDragging || !this.dragPreviewPosition) {
        return [];
      }
      
      try {
        if (this.draggingPointIndex === 0) {
          // 🎯 拖拽中心点：计算新边界点位置，保持相对方向和距离
          const originalCenter = this.drawingCoordinates[0];
          const originalEdge = this.drawingCoordinates[1];
          const newCenter = this.dragPreviewPosition;
          
          // 计算原始的方向偏移
          const latOffset = originalEdge[0] - originalCenter[0];
          const lngOffset = originalEdge[1] - originalCenter[1];
          
          // 计算新的边界点：新中心点 + 相同的偏移
          const newEdgePoint = [
            newCenter[0] + latOffset,
            newCenter[1] + lngOffset
          ];
          
          return [newCenter, newEdgePoint];
        } else {
          // 拖拽边界点：从原中心点到新边界点
          const centerPoint = this.drawingCoordinates[0];
          const edgePoint = this.dragPreviewPosition;
          return [centerPoint, edgePoint];
        }
      } catch (error) {
        console.warn('获取圆形拖拽预览半径线段出错:', error);
        return [];
      }
    },
    
    // 🎯 计算两点间的距离（单位：度）
    calculateDistance(point1, point2) {
      const lat1 = point1[0];
      const lng1 = point1[1];
      const lat2 = point2[0];
      const lng2 = point2[1];
      
      // 简单的欧几里得距离计算（适用于小范围地图）
      const deltaLat = lat2 - lat1;
      const deltaLng = lng2 - lng1;
      return Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng);
    },
    
    // 🎯 计算两点间的距离（单位：米，使用Haversine公式）
    calculateDistanceInMeters(point1, point2) {
      const lat1 = point1[0] * Math.PI / 180; // 转换为弧度
      const lng1 = point1[1] * Math.PI / 180;
      const lat2 = point2[0] * Math.PI / 180;
      const lng2 = point2[1] * Math.PI / 180;
      
      const deltaLat = lat2 - lat1;
      const deltaLng = lng2 - lng1;
      
      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      
      const earthRadius = 6371000; // 地球半径，单位：米
      return earthRadius * c;
    },
    
    // 🎯 生成圆形的近似多边形坐标
    generateCirclePolygon(center, radius, segments = 64) {
      const points = [];
      const angleStep = (2 * Math.PI) / segments;
      
      for (let i = 0; i <= segments; i++) {
        const angle = i * angleStep;
        const lat = center[0] + radius * Math.cos(angle);
        const lng = center[1] + radius * Math.sin(angle);
        points.push([lat, lng]);
      }
      
      return points;
    },
    
    // 🎯 计算拖拽时的鼠标样式
    getDragCursorStyle(dragIndex) {
      if (!this.isRectangleType) return 'move';
      
      // 根据拖拽的顶点返回不同的双箭头样式
      switch (dragIndex) {
        case 0: // 左上角
        case 2: // 右下角
          return 'nw-resize'; // 左上-右下双箭头
        case 1: // 右上角  
        case 3: // 左下角
          return 'ne-resize'; // 右上-左下双箭头
        default:
          return 'move';
      }
    },
    
    // 🎯 计算矩形拖拽预览坐标
    calculateRectangleDragPreview(dragIndex, mousePosition) {
      if (!this.isRectangleType || !this.drawingCoordinates || 
          (this.drawingCoordinates.length !== 4 && this.drawingCoordinates.length !== 5)) {
                 console.warn('🚫 矩形拖拽预览：坐标数组长度不符合要求，长度:', this.drawingCoordinates?.length);
        return null;
      }
      
      try {
                 // 🎯 处理已保存矩形（5个点）和新绘制矩形（4个点）
         let workingPoints;
         if (this.drawingCoordinates.length === 5) {
           // 已保存的矩形有闭合点，取前4个点
           workingPoints = this.drawingCoordinates.slice(0, 4);
         } else {
           // 新绘制的矩形
           workingPoints = [...this.drawingCoordinates];
         }
         
         // 🎯 验证拖拽索引是否有效（应该在0-3范围内）
         if (dragIndex < 0 || dragIndex >= 4) {
           console.warn('🚫 无效的拖拽索引:', dragIndex, '，矩形只有4个有效顶点');
           return null;
         }
        
        const originalPoints = [...workingPoints];
        // 矩形顺序：[左上, 右上, 右下, 左下]
        
        let newTopLeft, newTopRight, newBottomRight, newBottomLeft;
        
        switch (dragIndex) {
                     case 0: // 拖拽左上角
             // 新的左上角 = 鼠标位置
             // 保持右下角不动，重新计算其他两个角
             newTopLeft = mousePosition;
             newBottomRight = originalPoints[2]; // 右下角保持不动
             newTopRight = [newTopLeft[0], newBottomRight[1]]; // 右上角：左上角的纬度，右下角的经度
             newBottomLeft = [newBottomRight[0], newTopLeft[1]]; // 左下角：右下角的纬度，左上角的经度
             break;
             
           case 1: // 拖拽右上角
             // 新的右上角 = 鼠标位置
             // 保持左下角不动，重新计算其他两个角
             newTopRight = mousePosition;
             newBottomLeft = originalPoints[3]; // 左下角保持不动
             newTopLeft = [newTopRight[0], newBottomLeft[1]]; // 左上角：右上角的纬度，左下角的经度
             newBottomRight = [newBottomLeft[0], newTopRight[1]]; // 右下角：左下角的纬度，右上角的经度
             break;
             
           case 2: // 拖拽右下角
             // 新的右下角 = 鼠标位置
             // 保持左上角不动，重新计算其他两个角
             newBottomRight = mousePosition;
             newTopLeft = originalPoints[0]; // 左上角保持不动
             newTopRight = [newTopLeft[0], newBottomRight[1]]; // 右上角：左上角的纬度，右下角的经度
             newBottomLeft = [newBottomRight[0], newTopLeft[1]]; // 左下角：右下角的纬度，左上角的经度
             break;
             
           case 3: // 拖拽左下角
             // 新的左下角 = 鼠标位置
             // 保持右上角不动，重新计算其他两个角
             newBottomLeft = mousePosition;
             newTopRight = originalPoints[1]; // 右上角保持不动
             newTopLeft = [newTopRight[0], newBottomLeft[1]]; // 左上角：右上角的纬度，左下角的经度
             newBottomRight = [newBottomLeft[0], newTopRight[1]]; // 右下角：左下角的纬度，右上角的经度
             break;
            
          default:
            return null;
        }
        
        // 返回新的矩形坐标
        const newRectangle = [newTopLeft, newTopRight, newBottomRight, newBottomLeft];
        
        return newRectangle;
        
      } catch (error) {
        console.error('计算矩形拖拽预览失败:', error);
        return null;
      }
    },
    
    // 🎯 处理绘制完成事件
    handleDrawingEnded(coordinates) {
      try {
        console.log('BridgeMap: 收到绘制完成事件，坐标数量:', coordinates ? coordinates.length : 0);
        console.log('BridgeMap: 当前几何类型:', this.currentGeometryType);
        
        // 设置绘制完成状态
        this.drawingCompleted = true;
        
        // 🎯 同步更新绘制模式到父组件
        this.$emit('update:drawing-mode', false);
        
        // 根据类型显示不同的完成消息
        let message = '';
        switch (this.currentGeometryType) {
          case 'LINESTRING':
            message = '线段绘制已完成，可以拖拽端点调整位置';
            break;
          case 'RECTANGLE':
            message = '矩形绘制已完成，可以拖拽顶点调整大小';
            break;
          case 'CIRCLE':
            message = '圆形绘制已完成，可以拖拽中心点或边界点调整位置和大小';
            break;
          default:
            message = '绘制已完成，现在您可以拖拽或右键删除各个点';
            break;
        }
        
        this.$message({
          type: 'success',
          message: message
        });
        
        // 强制更新视图
        this.$forceUpdate();
        
      } catch (error) {
        console.error('处理绘制完成事件出错:', error);
      }
    },
    
    // 获取点的样式
    getPointStyle(point, index) {
      if (!this.map || !point) return { display: 'none' };
      try {
        // 检查点格式并确保是有效的坐标数组
        if (!Array.isArray(point) || point.length < 2) {
          return { display: 'none' };
        }
        
        // 确保坐标是数字
        const lat = Number(point[0]);
        const lng = Number(point[1]);
        if (isNaN(lat) || isNaN(lng)) {
          return { display: 'none' };
        }
        
        // 转换地理坐标为页面像素坐标
        const pixelPoint = this.map.latLngToContainerPoint([lat, lng]);
        
        // 检查是否为点标注模式 - 通过pointMarkers和坐标长度判断
        if (this.pointMarkers.length > 0 && this.drawingCoordinates.length === 1) {
          // 点标注模式下，根据是否绘制完成返回不同的样式
          // 已完成绘制，显示包围正方形
          if (this.drawingCompleted) {
            return {
              display: 'block',
              left: (pixelPoint.x - 0) + 'px', // 向左偏移16px配合iconAnchor: [36, 36]
              top: (pixelPoint.y - 0) + 'px', // 向上偏移16px配合iconAnchor: [36, 36]
              // CSS中margin-left: -20px, margin-top: -20px 加上这里的-16px偏移
              // 总偏移为-36px，正好配合iconAnchor: [36, 36]
              boxSizing: 'border-box'
            };
          } else {
            // 未完成绘制，不显示点标注的拖拽点
            return { display: 'none' };
          }
        }
        
        // 判断是否是正在拖拽的点
        const isDragging = this.isPointDragging && this.draggingPointIndex === index;
        
        // 多边形、线段等常规模式下的点样式
        const style = {
          left: `${pixelPoint.x}px`,
          top: `${pixelPoint.y}px`,
          backgroundColor: isDragging ? 'transparent' : 'white',
          border: isDragging ? '2px dashed #ff6600' : `2px solid ${this.getFixedColor()}`,
        };
        
        // 🎯 为矩形顶点添加特殊的鼠标样式（仅在非拖拽状态下设置，避免与全局cursor冲突）
        if (this.isRectangleType && this.drawingCompleted && !this.isPointDragging) {
          style.cursor = this.getDragCursorStyle(index);
        }
        
        return style;
      } catch (error) {
        console.warn('计算点样式出错:', error);
        return { display: 'none' };
      }
    },

    // 获取点标注icon的样式 - 严格按照getPointStyle的实现计算位置
    getPointMarkerIconStyle(marker) {
      if (!this.map || !marker || !marker.position) return { display: 'none' };
      
      try {
        // 检查坐标格式
        if (!Array.isArray(marker.position) || marker.position.length < 2) {
          return { display: 'none' };
        }
        
        // 确保坐标是数字
        const lat = Number(marker.position[0]);
        const lng = Number(marker.position[1]);
        if (isNaN(lat) || isNaN(lng)) {
          return { display: 'none' };
        }
        
        // 转换地理坐标为页面像素坐标 - 与getPointStyle完全一致
        const pixelPoint = this.map.latLngToContainerPoint([lat, lng]);
        
        // 获取icon尺寸，默认为36x36
        const iconSize = marker.iconSize || [36, 36];
        const iconWidth = iconSize[0];
        const iconHeight = iconSize[1];
        
        // 计算居中位置 - icon的中心点应该在地理坐标对应的像素点上
        const left = pixelPoint.x - (iconWidth / 2);
        const top = pixelPoint.y - (iconHeight / 2);
        
        return {
          position: 'absolute',
          left: left + 'px',
          top: top + 'px',
          width: iconWidth + 'px',
          height: iconHeight + 'px',
          display: 'flex',
          zIndex: 1000,
          pointerEvents: 'auto',
          fontSize: Math.min(iconWidth * 0.6, iconHeight * 0.6) + 'px', // 根据icon尺寸调整字体大小
          color: marker.color || '#409EFF',
          alignItems: 'center',
          justifyContent: 'center'
        };
      } catch (error) {
        console.warn('计算点标注icon样式出错:', error);
        return { display: 'none' };
      }
    },

    getFixedColor(){
      if(this.currentDrawingColor) {
        // if(this.currentDrawingColor==='#e74c3c'){
        //   debugger;
        // }
        return this.currentDrawingColor;
      }
      const currentArea = bridgeMapStore.currentArea || this.currentArea;
      if(currentArea){
        const areaId = currentArea.id;
        return bridgeMapStore.actions.getColorByAreaInfo(areaId, this.currentAreaType);
      }
      else{
        return bridgeMapStore.actions.getColorByAreaInfo(null, this.currentAreaType);
      }
    },
    
    // 处理点的鼠标按下事件
    handlePointMouseDown(event, index) {
      // 只有在绘制完成状态才能拖拽
      if (!this.drawingCompleted) {
        return;
      }
      
      // 阻止事件冒泡，但不阻止默认行为（需要保留拖拽功能）
      event.stopPropagation();
      // 注释掉preventDefault，否则会阻止dragstart事件
      // event.preventDefault();
      
      console.log('点鼠标按下，索引:', index);
      
      // 设置按下状态
      this.isMouseDown = true;
      this.selectedPointIndex = index;
      
      // 🎯 设置拖拽时的鼠标样式
      if (this.isRectangleType) {
        const cursor = this.getDragCursorStyle(index);
        document.body.style.cursor = cursor;
        console.log(`设置矩形拖拽鼠标样式: ${cursor} (顶点${index})`);
      } else if (this.currentGeometryType === 'POINT') {
        // 点标注模式：根据几何类型判断，而不是依赖pointMarkers数组
        document.body.style.cursor = 'grab';
        console.log('设置点标注拖拽鼠标样式: grab');
      } else {
        // 其他类型（多边形、线段等）：设置为移动样式
        document.body.style.cursor = 'move';
        console.log('设置常规拖拽鼠标样式: move');
      }
      
      // 禁用地图拖拽功能
      if (this.map && this.map.dragging) {
        console.log('鼠标按下时临时禁用地图拖拽功能');
        this.map.dragging.disable();
        this.mapDraggingDisabled = true;
      }
      
      // 如果是点标注，特殊处理 - 通过pointMarkers和坐标长度判断
      if (this.pointMarkers.length > 0 && this.drawingCoordinates.length === 1) {
        // 不需要找前后点，因为只有一个点
        this.connectedPointsBefore = null;
        this.connectedPointsAfter = null;
      } else {
        // 找到与当前点相连的前后点索引
        this.findConnectedPoints(index);
      }
    },
    
    // 找出与拖拽点相连的前后点索引
    findConnectedPoints(index) {
      const total = this.drawingCoordinates.length;
      
      if (total <= 1) {
        this.connectedPointsBefore = null;
        this.connectedPointsAfter = null;
        return;
      }
      
      // 如果是第一个点
      if (index === 0) {
        this.connectedPointsBefore = total - 1; // 最后一个点
        this.connectedPointsAfter = 1; // 第二个点
      } 
      // 如果是最后一个点
      else if (index === total - 1) {
        this.connectedPointsBefore = index - 1; // 倒数第二个点
        this.connectedPointsAfter = 0; // 第一个点
      }
      // 中间的点
      else {
        this.connectedPointsBefore = index - 1;
        this.connectedPointsAfter = index + 1;
      }
      
      console.log('相连点索引:', '前:', this.connectedPointsBefore, '后:', this.connectedPointsAfter);
    },
    
    // 处理点拖拽开始
    handlePointDragStart(event, index) {
      console.log('🚀 handlePointDragStart 被调用，索引:', index);
      console.log('🔍 当前几何类型识别:', {
        currentGeometryType: this.currentGeometryType,
        isRectangleType: this.isRectangleType,
        currentGeometryTypeProp: this.currentGeometryTypeProp,
        markType: bridgeMapStore.state.markType,
        currentAreaType: this.currentAreaType,
        coordinatesLength: this.drawingCoordinates?.length
      });
      
      // 只有在绘制完成状态才能拖拽
      if (!this.drawingCompleted) {
        console.log('🚫 绘制未完成，阻止拖拽');
        event.preventDefault();
        return;
      }
      
      console.log('✅ 开始设置拖拽状态');
      
      // 记录拖拽开始的点和索引
      this.draggingPointIndex = index;
      this.dragStartPoint = this.drawingCoordinates[index];
      this.dragPreviewPosition = [...this.dragStartPoint]; // 初始化预览位置
      
      // 设置正在拖拽标记
      this.isPointDragging = true;
      
      // 🎯 设置拖拽进行中的鼠标样式
      if (this.isRectangleType) {
        const cursor = this.getDragCursorStyle(index);
        document.body.style.cursor = cursor;
        console.log(`拖拽开始 - 矩形样式: ${cursor}`);
      } else if (this.currentGeometryType === 'POINT') {
        // 点标注模式：根据几何类型判断，设置为抓取中样式
        document.body.style.cursor = 'grabbing';
        console.log('拖拽开始 - 点标注样式: grabbing');
      } else {
        // 其他类型：设置为移动中样式
        document.body.style.cursor = 'move';
        console.log('拖拽开始 - 常规样式: move');
      }
      
      console.log('🔄 拖拽状态已设置:', {
        draggingPointIndex: this.draggingPointIndex,
        isPointDragging: this.isPointDragging,
        selectedPointIndex: this.selectedPointIndex
      });
      
      // 🎯 矩形拖拽开始时的调试信息
      if (this.isRectangleType) {
        console.log('🎯 矩形拖拽开始:', {
          index: index,
          isRectangleType: this.isRectangleType,
          isPointDragging: this.isPointDragging,
          selectedPointIndex: this.selectedPointIndex,
          originalCoords: this.drawingCoordinates
        });
      }
      
      // 存储拖拽数据
      event.dataTransfer.setData('text/plain', index);
      
      // 设置自定义拖拽图像
      const dragImage = new Image();
      dragImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // 透明1x1像素
      event.dataTransfer.setDragImage(dragImage, 0, 0);
      
      // 设置拖拽效果
      event.dataTransfer.effectAllowed = 'move';
      
      // 禁用地图拖拽功能
      if (this.map && this.map.dragging) {
        console.log('临时禁用地图拖拽功能');
        this.map.dragging.disable();
        this.mapDraggingDisabled = true;
      }
      
      // 找到与当前点相连的前后点索引
      this.findConnectedPoints(index);
      
      // 防止事件冒泡和默认行为
      event.stopPropagation();
    },
    
    // 处理点拖拽过程中
    handlePointDrag(event) {
      if (!this.isPointDragging) return;
      
      // 防止事件冒泡到地图
      event.stopPropagation();
      event.preventDefault();
      
      try {
        // 获取鼠标当前位置相对于地图容器的坐标
        const mapContainer = this.map._container;
        const rect = mapContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // 将像素坐标转换为地理坐标
        const latlng = this.map.containerPointToLatLng([x, y]);
        
        if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
          // 更新拖拽预览位置
          this.dragPreviewPosition = [latlng.lat, latlng.lng];
          
          // 🎯 矩形拖拽预览
          if (this.isRectangleType && this.isPointDragging && this.selectedPointIndex !== null) {
            const newRectangleCoords = this.calculateRectangleDragPreview(this.selectedPointIndex, [latlng.lat, latlng.lng]);
            if (newRectangleCoords && newRectangleCoords.length === 4) {
              // 确保矩形闭合以正确渲染
              this.rectangleDragPreview = [...newRectangleCoords, newRectangleCoords[0]];
            }
          }
        }
      } catch (error) {
        console.error('handlePointDrag 处理出错:', error);
      }
    },
    
    // 处理点拖拽结束
    handlePointDragEnd(event, index) {
      // 只有在绘制完成状态才能拖拽
      if (!this.drawingCompleted || this.draggingPointIndex !== index) {
        this.resetDragState();
        return;
      }
      
      // 阻止事件默认行为
      event.preventDefault();
      event.stopPropagation();
      
      try {
        // 获取鼠标当前位置相对于地图容器的坐标
        const mapContainer = this.map._container;
        const rect = mapContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // 将像素坐标转换为地理坐标
        const latlng = this.map.containerPointToLatLng([x, y]);
        
        // 确保是有效的坐标
        if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
          // 更新坐标数组
          const updatedCoordinates = [...this.drawingCoordinates];
          
          // 处理点标注的特殊情况 - 通过pointMarkers和坐标长度判断
          if (this.pointMarkers.length > 0 && updatedCoordinates.length === 1) {
            const newPosition = [latlng.lat, latlng.lng];
            
            // 对于点标注，直接更新单点坐标
            updatedCoordinates[0] = newPosition;
            
            // 同时更新点标注标记的位置
            if (this.pointMarkers.length > 0) {
              const marker = this.pointMarkers[0];
              marker.position = newPosition;
              
              // 触发一个事件通知位置已更新，可能有其他组件需要同步
              this.$emit('point-marker-moved', {
                markerId: marker.id,
                position: newPosition
              });
            }
          } else if (this.isRectangleType) {
            // 🎯 矩形类型特殊处理：使用预览中计算的完整矩形坐标
            const newRectangleCoords = this.calculateRectangleDragPreview(index, [latlng.lat, latlng.lng]);
            if (newRectangleCoords && newRectangleCoords.length === 4) {
              // 🎯 根据原始坐标长度决定如何更新
              if (updatedCoordinates.length === 5) {
                // 已保存的矩形有闭合点，替换前4个点，保持闭合
                updatedCoordinates.splice(0, 4, ...newRectangleCoords);
                updatedCoordinates[4] = newRectangleCoords[0]; // 更新闭合点
                console.log('✅ 矩形拖拽完成，更新5点矩形（保持闭合）');
              } else {
                // 新绘制的矩形，直接替换4个点
                updatedCoordinates.splice(0, 4, ...newRectangleCoords);
                console.log('✅ 矩形拖拽完成，更新4点矩形');
              }
            } else {
              console.warn('矩形拖拽预览计算失败，回退到单点更新');
              updatedCoordinates[index] = [latlng.lat, latlng.lng];
            }
          } else if (this.isCircleType) {
            // 🎯 圆形类型特殊处理
            const newPosition = [latlng.lat, latlng.lng];
            if (index === 0) {
              // 拖拽中心点：移动整个圆形
              const centerOffset = [
                newPosition[0] - updatedCoordinates[0][0],
                newPosition[1] - updatedCoordinates[0][1]
              ];
              updatedCoordinates[0] = newPosition; // 更新中心点
              if (updatedCoordinates.length >= 2) {
                // 同时移动边界点
                updatedCoordinates[1] = [
                  updatedCoordinates[1][0] + centerOffset[0],
                  updatedCoordinates[1][1] + centerOffset[1]
                ];
              }
              console.log('✅ 圆形中心点拖拽完成，整体移动');
            } else if (index === 1) {
              // 拖拽边界点：调整半径
              updatedCoordinates[1] = newPosition;
              console.log('✅ 圆形边界点拖拽完成，半径调整');
            }
          } else {
            // 正常的多点区域（线段、多边形等）
            updatedCoordinates[index] = [latlng.lat, latlng.lng];
          }
          
          // 更新绘制坐标
          this.$emit('update:drawing-coordinates', updatedCoordinates);
        } else {
          console.warn('无效的拖拽位置:', latlng);
        }
      } catch (error) {
        console.error('处理拖拽结束出错:', error);
      } finally {
        // 重置拖拽状态
        this.resetDragState();
      }
    },
    
    // 重置拖拽相关状态
    resetDragState() {
      // 清除拖拽状态
      this.draggingPointIndex = null;
      this.dragStartPoint = null;
      this.isPointDragging = false;
      this.isMouseDown = false;
      this.selectedPointIndex = null;
      this.dragPreviewPosition = null;
      this.connectedPointsBefore = null;
      this.connectedPointsAfter = null;
      
      // 🎯 清除矩形拖拽预览
      this.rectangleDragPreview = null;
      
      // 🎯 重置拖拽状态日志标识
      this._loggedDragState = false;
      
      // 🎯 恢复鼠标样式
      document.body.style.cursor = 'default';
      
      // 移除预览点
      const previewPoint = document.querySelector('.drag-preview-marker');
      if (previewPoint && previewPoint.parentNode) {
        previewPoint.parentNode.removeChild(previewPoint);
      }
      
      // 恢复地图拖拽功能
      this.restoreMapDragging();
    },
    
    // 恢复地图拖拽功能
    restoreMapDragging() {
      if (this.map && this.map.dragging && this.mapDraggingDisabled) {
        console.log('恢复地图拖拽功能');
        this.map.dragging.enable();
        this.mapDraggingDisabled = false;
      }
    },
    
    // 注册事件监听
    setupEventListeners() {
      // 不再监听MAP_READY事件，避免循环调用
      
      // 监听区域加载事件
      BridgeEventBus.$on(BRIDGE_EVENTS.AREA_DATA_LOADED, this.handleAreaDataLoaded);
      
      // 监听位置拾取开始事件
      BridgeEventBus.$on(BRIDGE_EVENTS.LOCATION_PICKING_STARTED, this.handleLocationPickingStarted);

      // 监听区域清除事件
      BridgeEventBus.$on(BRIDGE_EVENTS.AREA_CLEARED, this.handleAreaCleared);
      BridgeEventBus.$on(BRIDGE_EVENTS.AREA_VISIBILITY_CHANGED, this.handleAreaVisibilityChanged);
      
      // 监听区域颜色变更事件
      BridgeEventBus.$on(BRIDGE_EVENTS.AREA_COLOR_CHANGED, this.handleAreaColorChanged);
      
      // 添加全局鼠标事件监听
      document.addEventListener('mousemove', this.handleGlobalMouseMove);
      document.addEventListener('mouseup', this.handleGlobalMouseUp);
    },
    
    // 注销事件监听
    removeEventListeners() {
      // 不再监听MAP_READY事件
      BridgeEventBus.$off(BRIDGE_EVENTS.AREA_DATA_LOADED, this.handleAreaDataLoaded);
      BridgeEventBus.$off(BRIDGE_EVENTS.LOCATION_PICKING_STARTED, this.handleLocationPickingStarted);
      BridgeEventBus.$off(BRIDGE_EVENTS.AREA_CLEARED, this.handleAreaCleared);
      BridgeEventBus.$off(BRIDGE_EVENTS.AREA_VISIBILITY_CHANGED, this.handleAreaVisibilityChanged);
      BridgeEventBus.$off(BRIDGE_EVENTS.AREA_COLOR_CHANGED, this.handleAreaColorChanged);
      
      // 移除地图事件监听
      if (this.map) {
        this.map.off('click', this.onMapClick);
        this.map.off('mousemove', this.handleMapMouseMove);
        this.map.off('contextmenu', this.handleContextMenu);
        this.map.off('moveend', this.updatePointsPosition);
        this.map.off('zoomend', this.updatePointsPosition);
        this.map.off('move', this.updatePointsPositionRealtime);
        this.map.off('zoom', this.updatePointsPositionRealtime);
        
        // 🎯 移除地图容器的拖拽事件监听
        const mapContainer = this.map._container;
        if (mapContainer) {
          mapContainer.removeEventListener('dragover', this.handleMapDragOver);
          mapContainer.removeEventListener('drop', this.handleMapDrop);
          console.log('已移除地图容器拖拽事件监听');
        }
      }
      
      // 移除全局鼠标事件监听
      document.removeEventListener('mousemove', this.handleGlobalMouseMove);
      document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    },
    
    // 处理区域数据加载事件
    handleAreaDataLoaded(areas) {
      // 使用节流防止短时间内多次处理同一事件
      if (this._processingAreaData) {
        console.log('区域数据加载事件节流 - 跳过重复处理');
        return;
      }
      
      this._processingAreaData = true;
      setTimeout(() => {
        this._processingAreaData = false;
      }, 200); // 200ms内不重复处理
      
      // 可以在这里实现自定义逻辑
      console.log('区域数据已加载:', areas.length);
    },
    
    // 处理位置拾取开始事件
    handleLocationPickingStarted() {
      // 可以显示提示信息等
      this.$message.info('请在地图上点击选择位置');
    },

    // 处理区域被清除事件
    handleAreaCleared(area) {
      console.log('BridgeMap: 接收到区域清除事件，区域ID:', area ? area.id : 'unknown');
      // 强制刷新地图视图
      if (this.map) {
        console.log('BridgeMap: 正在刷新地图视图...');
        this.map.invalidateSize();
        
        // 移除当前选中区域从绘制层
        if (area && area.id) {
          // 创建临时数组，排除被清除的区域
          const updatedAreas = this.savedAreas.filter(a => a.id !== area.id);
          
          // 临时将savedAreas替换为不包含清除区域的数组，触发视图更新
          this.$emit('temp-update-areas', updatedAreas);
          
          console.log(`BridgeMap: 已尝试从图层移除区域 ID:${area.id}`);
        }
      }
    },
    
    // 地图加载完成事件
    onMapReady(mapInstance) {
      if (!this.map) {
        this.map = mapInstance;
        
        // 初始化文本标注防重叠管理器（区域名称标注避让）
        this.mapLabelManager = createMapLabelManager(mapInstance, {
          collisionSelector: '.area-label',
          drawReferenceLine: true
        });
        
        // 设置点击事件监听器
        this.map.on('click', this.onMapClick);
        
        // 添加鼠标移动监听器（用于显示预览线段）
        this.map.on('mousemove', this.handleMapMouseMove);
        
        // 添加右键菜单监听器
        this.map.on('contextmenu', this.handleContextMenu);
        
        // 地图移动事件 - 用于更新点标记位置
        this.map.on('moveend', this.updatePointsPosition);
        this.map.on('zoomend', this.updatePointsPosition);
        // 添加实时移动和缩放事件监听，确保点标注跟随地图移动
        this.map.on('move', this.updatePointsPositionRealtime);
        this.map.on('zoom', this.updatePointsPositionRealtime);
        
        // 🎯 添加地图容器的拖拽事件监听，解决HTML5拖拽光标问题
        const mapContainer = this.map._container;
        if (mapContainer) {
          mapContainer.addEventListener('dragover', this.handleMapDragOver);
          mapContainer.addEventListener('drop', this.handleMapDrop);
          console.log('已添加地图容器拖拽事件监听');
        }
        
        // 同步到状态存储，但不再通过事件总线广播
        // 避免与BridgeManagement.vue中的handleMapReady形成循环调用
        bridgeMapStore.actions.setMap(mapInstance);
        
        // 当父组件需要时触发事件
        this.$emit('map-ready', mapInstance);
        
        // 如果有坐标，将地图中心定位到桥梁位置
        if (this.isEdit && this.marker) {
          this.map.setView(this.marker, 15);
        }
      }
    },
    
    // 更新点标记位置 - 在地图移动或缩放后调用
    updatePointsPosition() {
      // 强制重绘，这会触发所有计算属性重新计算，包括pointMarkerIcons
      this.mapUpdateCounter++;
      this.$forceUpdate();
    },

    // 实时更新点标记位置 - 在地图拖动和缩放过程中调用
    updatePointsPositionRealtime() {
      // 清除之前的定时器
      if (this.realtimeUpdateTimer) {
        clearTimeout(this.realtimeUpdateTimer);
      }
      
      // 使用节流机制，减少更新频率
      this.realtimeUpdateTimer = setTimeout(() => {
        // 增加更新计数器，触发计算属性重新计算
        this.mapUpdateCounter++;
        // 强制更新视图
        this.$forceUpdate();
        this.realtimeUpdateTimer = null;
      }, 16); // 约60fps的更新频率
    },
    
    // 处理右键菜单
    handleContextMenu(event) {
      // 只有在绘制模式或编辑完成模式下才显示右键菜单
      if (!this.drawingMode && !this.drawingCompleted) return;
      
      // 阻止默认右键菜单
      event.originalEvent.preventDefault();
      
      // 获取鼠标位置
      const { clientX, clientY } = event.originalEvent;
      
      // 设置菜单位置
      this.contextMenuPosition = {
        top: clientY,
        left: clientX
      };
      
      // 重置选中点的索引（普通右键不选中点）
      this.selectedPointIndex = null;
      
      // 显示菜单
      this.contextMenuVisible = true;
    },
    
    // 处理点标记的右键菜单
    handlePointContextMenu(event, index) {
      // 阻止默认右键菜单
      event.preventDefault();
      event.stopPropagation();
      
      console.log('点标记右键菜单，索引:', index, '绘制完成状态:', this.drawingCompleted);
      
      // 只有在绘制完成后才处理点右键菜单
      if (!this.drawingCompleted) return;
      
      // 获取鼠标位置
      const { clientX, clientY } = event;
      
      // 设置菜单位置
      this.contextMenuPosition = {
        top: clientY,
        left: clientX
      };
      
      // 设置当前选中的点索引
      this.selectedPointIndex = index;
      
      // 显示菜单
      this.contextMenuVisible = true;
      
      // 禁用默认浏览器右键菜单
      return false;
    },
    
    // 隐藏右键菜单
    hideContextMenu() {
      this.contextMenuVisible = false;
      // 同时重置选中点索引
      this.selectedPointIndex = null;
    },
    
    // 删除指定点
    deletePoint(index) {
      if (index === null || index < 0 || index >= this.drawingCoordinates.length) {
        return;
      }
      
      // 只有在绘制完成后才允许删除点
      if (!this.drawingCompleted) return;
      
      // 🎯 添加类型限制检查
      if (this.isLineStringType) {
        this.$message.warning('线段类型必须保持2个点，不允许删除');
        return;
      }
      
      if (this.isRectangleType) {
        this.$message.warning('矩形类型必须保持4个点，不允许删除');
        return;
      }
      
      if (this.isCircleType) {
        this.$message.warning('圆形类型必须保持2个点，不允许删除');
        return;
      }
      
      // 确保删除点后仍然至少有3个点
      if (this.drawingCoordinates.length <= 3) {
        this.$message.warning('区域至少需要3个点，无法删除');
        return;
      }
      
      // 创建新的坐标数组，而不是直接修改props
      const newCoordinates = [...this.drawingCoordinates];
      
      // 删除指定点
      newCoordinates.splice(index, 1);
      
      // 更新绘制坐标并通知父组件
      this.$emit('update:drawing-coordinates', newCoordinates);
      
      // 重置选中点索引
      this.selectedPointIndex = null;
      
      // 显示成功消息
      this.$message.success('已删除选中的点');
    },
    
    // 处理右键菜单操作
    handleContextMenuAction(action) {
      // 根据操作类型执行不同的功能
      let canFinish = false; // 移动声明到switch外部
      
      switch (action) {
        case 'finish':
          // 🎯 根据类型检查是否可以完成绘制
          switch (this.currentGeometryType) {
            case 'LINESTRING':
              canFinish = this.drawingCoordinates.length >= 2;
              break;
            case 'RECTANGLE':
              canFinish = this.drawingCoordinates.length >= 4;
              break;
            case 'CIRCLE':
              canFinish = this.drawingCoordinates.length >= 2;
              break;
            default: // POLYGON
              canFinish = this.drawingCoordinates.length >= 3;
              break;
          }
          
          if (canFinish) {
            // 设置填充多边形效果（线段不需要填充）
            this.shouldShowFilledPolygon = this.currentGeometryType !== 'LINESTRING';
            this.finishDrawing();
          }
          break;
        case 'undo':
          if (this.drawingCoordinates.length > 0 && !this.drawingCompleted) {
            this.undoLastPoint();
          }
          break;
        case 'redraw':
          if (!this.drawingCompleted) {
            this.redrawAll();
          } else {
            // 已完成状态下，重新开始编辑
            this.restartDrawing();
          }
          break;
        case 'deletePoint':
          if (this.selectedPointIndex !== null && this.drawingCompleted) {
            console.log('删除点，索引:', this.selectedPointIndex);
            this.deletePoint(this.selectedPointIndex);
          }
          break;
        case 'addNewPoint':
          if (this.selectedPointIndex !== null && this.drawingCompleted) {
            console.log('添加新点，在索引后:', this.selectedPointIndex);
            this.addNewPoint(this.selectedPointIndex);
          }
          break;
        case 'saveEdit':
          if (this.drawingCompleted && this.drawingCoordinates.length >= 3) {
            // 保存编辑后的区域
            this.saveEditedArea();
          }
          break;
        case 'cancelEdit':
          if (this.drawingCompleted) {
            // 取消编辑
            this.cancelEditing();
          }
          break;
      }
      
      // 关闭菜单
      this.hideContextMenu();
    },
    
    // 在指定点后添加一个新点
    addNewPoint(index) {
      if (index === null || index < 0 || index >= this.drawingCoordinates.length) {
        return;
      }
      
      // 只有在绘制完成后才允许添加点
      if (!this.drawingCompleted) return;
      
      // 🎯 添加类型限制检查
      if (this.isLineStringType || this.isRectangleType || this.isCircleType) {
        let typeName = this.isLineStringType ? '线段' : (this.isRectangleType ? '矩形' : '圆形');
        this.$message.warning(`${typeName}类型不允许添加新的点`);
        return;
      }
      
      try {
        // 获取当前点和下一个点的坐标
        const currentPoint = this.drawingCoordinates[index];
        let nextIndex = (index + 1) % this.drawingCoordinates.length;
        const nextPoint = this.drawingCoordinates[nextIndex];
        
        // 创建新的坐标数组，而不是直接修改props
        const newCoordinates = [...this.drawingCoordinates];
        
        // 在当前点后插入新点（初始位置与当前点相同，但稍微偏移一点便于区分）
        // 计算当前点与下一个点的中间位置
        const midPoint = [
          currentPoint[0] + (nextPoint[0] - currentPoint[0]) * 0.5, 
          currentPoint[1] + (nextPoint[1] - currentPoint[1]) * 0.5
        ];
        
        // 在当前点后插入新点
        newCoordinates.splice(index + 1, 0, midPoint);
        
        // 更新绘制坐标并通知父组件
        this.$emit('update:drawing-coordinates', newCoordinates);
        
        // 设置新添加的点为选中状态，方便继续操作
        this.selectedPointIndex = index + 1;
        
        // 设置拖拽预览位置
        this.dragPreviewPosition = midPoint;
        
        // 更新与选中点相连的前后点
        this.findConnectedPoints(this.selectedPointIndex);
        
        // 显示成功消息
        this.$message.success('已添加新点，可拖动调整位置');
        
        // 强制更新视图
        this.$forceUpdate();
      } catch (error) {
        console.error('添加新点出错:', error);
        this.$message.error('添加新点失败');
      }
    },
    
    // 重新开始编辑当前区域
    restartDrawing() {
      this.$confirm('确定要重新开始编辑当前区域？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 保持当前坐标，但切换回绘制模式
        this.drawingCompleted = false;
        // 通过事件通知父组件更新drawingMode
        this.$emit('update:drawing-mode', true);
        
        this.$message({
          type: 'info',
          message: '已切换回编辑模式，您可以继续添加或调整点'
        });
      }).catch(() => {
        // 用户取消操作
      });
    },
    
    // 保存编辑后的区域
    saveEditedArea() {
      // 确保有足够的点
      if (this.drawingCoordinates.length < 3) {
        this.$message.warning('区域至少需要3个点');
        return;
      }
      
      // 发出保存事件，让父组件处理保存逻辑
      this.$emit('save-edited-area', this.drawingCoordinates);
      
      // 提示用户
      this.$message({
        type: 'success',
        message: '已保存编辑后的区域'
      });
      
      // 重置状态
      this.drawingCompleted = false;
      // 通过事件通知父组件更新drawingMode
      this.$emit('update:drawing-mode', false);
    },
    
    // 取消编辑
    cancelEditing() {
      this.$confirm('确定要取消编辑？未保存的修改将丢失', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 退出编辑模式
        this.drawingCompleted = false;
        // 通过事件通知父组件更新drawingMode
        this.$emit('update:drawing-mode', false);
        
        // 通知父组件取消编辑
        this.$emit('cancel-editing');
        
        // 清除当前绘制内容
        this.clearDrawing();
      }).catch(() => {
        // 用户取消操作
      });
    },
    
    // 全部重绘
    redrawAll() {
      // 确认对话框
      this.$confirm('确定要清空当前绘制并重新开始？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 清空当前绘制
        this.clearDrawing();
        
        // 保持绘制模式开启
        if (!this.drawingMode) {
          this.$emit('toggle-drawing', true);
        }
        
        // 重置绘制完成状态
        this.drawingCompleted = false;
        
        this.$message({
          type: 'success',
          message: '已清空绘制内容，可以重新开始'
        });
      }).catch(() => {
        // 用户取消操作
      });
    },
    
    // 处理地图鼠标移动事件 - 用于绘制预览线段
    handleMapMouseMove(event) {
      // 🎯 处理测距工具鼠标移动
      if (this.isMeasureMode && this.measureDrawing && this.measureCoordinates.length === 1 && event.latlng) {
        this.measureMousePosition = [parseFloat(event.latlng.lat), parseFloat(event.latlng.lng)];
        
        // 🎯 直接使用Leaflet API更新预览线段
        if (this.map) {
          // 移除旧的预览线段和标签
          if (this.measurePreviewPolyline) {
            this.map.removeLayer(this.measurePreviewPolyline);
          }
          if (this.measurePreviewTooltip) {
            this.map.removeLayer(this.measurePreviewTooltip);
          }
          
          // 添加新的预览线段
          this.measurePreviewPolyline = this.L.polyline([this.measureCoordinates[0], this.measureMousePosition], {
            color: '#ff4444',
            weight: 2,
            dashArray: '8, 6',
            opacity: 0.6
          }).addTo(this.map);
          
          // 计算预览距离和角度
          const previewDistance = this.calculateDistanceInMeters(this.measureCoordinates[0], this.measureMousePosition);
          const previewBearing = this.calculateBearing(this.measureCoordinates[0], this.measureMousePosition);
          
          // 在鼠标位置显示半透明的预览信息
          this.measurePreviewTooltip = this.L.marker(this.measureMousePosition, {
            icon: this.getMeasurePreviewTooltipIcon(previewDistance, previewBearing)
          }).addTo(this.map);
        }
        
        return;
      }
      
      if (this.drawingMode && this.drawingCoordinates.length > 0 && event.latlng && !this.drawingCompleted) {
        // 如果是点标注，不显示预览线段
        if (this.pointMarkers.length === 0 || this.drawingCoordinates.length !== 1) {
          // 更新当前鼠标位置
          this.currentMousePosition = [event.latlng.lat, event.latlng.lng];
        } else {
          // 点标注模式下，不显示预览线段
          this.currentMousePosition = null;
        }
      }
    },
    
    // 处理全局鼠标移动事件
    handleGlobalMouseMove(event) {
      // 如果没有在拖拽点或没有按下鼠标，则不处理
      if ((!this.isPointDragging && !this.isMouseDown) || 
          (this.draggingPointIndex === null && this.selectedPointIndex === null) || 
          !this.map) {
        return;
      }
      
      try {
        // 获取当前操作的点索引
        const currentPointIndex = this.isPointDragging ? this.draggingPointIndex : this.selectedPointIndex;
        
        // 获取鼠标当前位置相对于地图容器的坐标
        const mapContainer = this.map._container;
        const rect = mapContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // 检查是否在地图容器内
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
          // 如果鼠标移出地图，不更新点位置
          return;
        }
        
        // 将像素坐标转换为地理坐标
        const latlng = this.map.containerPointToLatLng([x, y]);
        
        if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
          // 更新拖拽预览位置
          this.dragPreviewPosition = [latlng.lat, latlng.lng];
          
          // 🎯 矩形拖拽预览
          if (this.isRectangleType && this.isPointDragging && this.selectedPointIndex !== null) {
            const newRectangleCoords = this.calculateRectangleDragPreview(this.selectedPointIndex, [latlng.lat, latlng.lng]);
            if (newRectangleCoords && newRectangleCoords.length === 4) {
              // 确保矩形闭合以正确渲染
              this.rectangleDragPreview = [...newRectangleCoords, newRectangleCoords[0]];
            }
          }
          
          // 创建或更新预览点
          this.$nextTick(() => {
            // 显示预览效果 - 预览点由CSS处理视觉效果
            const previewPoint = document.querySelector('.drag-preview-marker');
            if (!previewPoint) {
              // 创建预览点
              const dragPreview = document.createElement('div');
              dragPreview.className = 'drag-preview-marker';
              dragPreview.style.left = x + 'px';
              dragPreview.style.top = y + 'px';
              mapContainer.appendChild(dragPreview);
            } else {
              // 更新预览点位置
              previewPoint.style.left = x + 'px';
              previewPoint.style.top = y + 'px';
            }
          });
          
          if (this.isPointDragging) {
            // 如果是正在拖拽中，更新坐标数组
            const updatedCoordinates = [...this.drawingCoordinates];
            
            // 处理点标注的特殊情况 - 通过pointMarkers和坐标长度判断
            if (this.pointMarkers.length > 0 && updatedCoordinates.length === 1) {
              // 对于点标注，直接更新单点坐标
              updatedCoordinates[0] = [latlng.lat, latlng.lng];
            } else {
              // 正常的多点区域
              updatedCoordinates[currentPointIndex] = [latlng.lat, latlng.lng];
            }
            
            // 更新绘制坐标（Vue的响应式系统会自动处理视图更新，无需强制刷新）
            this.$emit('update:drawing-coordinates', updatedCoordinates);
          }
        }
      } catch (error) {
        console.warn('处理拖拽鼠标移动出错:', error);
      }
    },
    
    // 处理全局鼠标释放事件
    handleGlobalMouseUp(event) {
      // 如果没有按下鼠标或正在拖拽中，不处理
      if (!this.isMouseDown || this.isPointDragging) {
        return;
      }
      
      // 如果是在mousedown之后，但没有进入拖拽状态
      if (this.selectedPointIndex !== null && this.drawingCompleted) {
        try {
          // 获取鼠标当前位置相对于地图容器的坐标
          const mapContainer = this.map._container;
          const rect = mapContainer.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;
          
          // 检查是否在地图容器内
          if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
            // 如果鼠标移出地图，不更新点位置
            this.resetDragState();
            return;
          }
          
          // 将像素坐标转换为地理坐标
          const latlng = this.map.containerPointToLatLng([x, y]);
          
                  if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
          // 🎯 准备新的坐标位置
          let newPosition = [latlng.lat, latlng.lng];
          
          // 更新坐标数组
          let updatedCoordinates = [...this.drawingCoordinates];
          
          // 处理点标注的特殊情况 - 通过pointMarkers和坐标长度判断
          if (this.pointMarkers.length > 0 && updatedCoordinates.length === 1) {
            // 对于点标注，直接更新单点坐标
            updatedCoordinates[0] = newPosition;
            
            // 同时更新点标注标记的位置
            if (this.pointMarkers.length > 0) {
              const marker = this.pointMarkers[0];
              marker.position = newPosition;
            }
          } else {
            // 🎯 根据几何类型处理拖拽
            if (this.isRectangleType && this.selectedPointIndex !== null) {
              // 矩形类型 - 保持直角
              updatedCoordinates = this.updateRectangleCoordinates(this.selectedPointIndex, newPosition);
            } else if (this.isLineStringType && this.selectedPointIndex !== null) {
              // 线段类型 - 允许自由拖拽端点
              updatedCoordinates[this.selectedPointIndex] = newPosition;
              console.log(`线段端点${this.selectedPointIndex}已更新到:`, newPosition);
            } else {
              // 正常的多点区域
              updatedCoordinates[this.selectedPointIndex] = newPosition;
            }
          }
          
          // 更新绘制坐标
          this.$emit('update:drawing-coordinates', updatedCoordinates);
          
          console.log('鼠标释放，更新点位置:', this.selectedPointIndex, '->', newPosition, '类型:', this.currentGeometryType);
          
          // 🎯 矩形拖拽完成后清除预览
          if (this.isRectangleType) {
            this.rectangleDragPreview = null;
          }
        }
        } catch (error) {
          console.error('处理鼠标释放事件出错:', error);
        }
      }
      
      // 重置拖拽状态
      this.resetDragState();
    },
    
    // 切换图层管理面板显示状态
    toggleLayerManager() {
      this.layerManagerVisible = !this.layerManagerVisible;
    },
    
    // 切换地图类型
    switchMapType(type) {
      if (this.currentMapType === type) return;
      
      this.currentMapType = type;
      
      // 直接更新mapType属性，BaseMap组件会通过watch监听变化并自动切换地图类型
      // 不再需要手动调用changeMapType方法
    },
    
    // 地图点击事件处理
    onMapClick(event) {
      try {
        // 如果右键菜单可见，则隐藏菜单并不处理点击
        if (this.contextMenuVisible) {
          this.hideContextMenu();
          return;
        }
        
        // 确保有效的事件对象
        if (!event || !event.latlng) {
          console.warn('无效的地图点击事件', event);
          return;
        }
        
        const latlng = event.latlng;
        
        // 🎯 处理测距模式（在区域编辑模式下也可用）
        if (this.isMeasureMode) {
          this.handleMeasureClick(latlng);
          return;
        }
        
        // 如果是已完成绘制状态，不处理点击（除非是测距模式）
        if (this.drawingCompleted) {
          return;
        }
        
        // 确保坐标对象有lat和lng属性
        if (typeof latlng.lat === 'undefined' || typeof latlng.lng === 'undefined') {
          console.warn('坐标对象缺少lat或lng属性', latlng);
          return;
        }
        
        // 🎯 添加类型限制检查
        if (this.drawingMode && !this.isPointMarkerMode) {
          // 检查是否超过最大允许点数
          if (this.drawingCoordinates.length >= this.maxAllowedPoints) {
            let typeName = '';
            switch (this.currentGeometryType) {
              case 'LINESTRING': typeName = '线段'; break;
              case 'RECTANGLE': typeName = '矩形'; break;
              case 'CIRCLE': typeName = '圆形'; break;
              default: typeName = '该类型'; break;
            }
            this.$message.warning(`${typeName}最多只能有${this.maxAllowedPoints}个点`);
            return;
          }
          
          // 🎯 智能提示逻辑：避免第二次点击时覆盖自动完成消息
          const isFirstClick = this.drawingCoordinates.length === 0;
          const isSecondClickForAutoComplete = this.drawingCoordinates.length === 1 && 
                                              (this.isLineStringType || this.isRectangleType || this.isCircleType);
          
          if (isFirstClick) {
            // 第一次点击：显示绘制提示
            if (this.isLineStringType) {
              this.$message.info('请点击第二个点完成线段绘制');
            } else if (this.isRectangleType) {
              this.$message.info('请点击对角位置完成矩形绘制');
            } else if (this.isCircleType) {
              this.$message.info('请点击边界位置完成圆形绘制');
            }
          } else if (isSecondClickForAutoComplete) {
            // 第二次点击且会自动完成：不显示提示，让store的自动完成消息优先
            console.log(`${this.currentGeometryType}: 第二次点击，跳过提示以优先显示自动完成消息`);
          }
        }
        

        
        // 通过事件总线广播点击事件
        BridgeEventBus.$emit(BRIDGE_EVENTS.MAP_CLICK, latlng);
        
        // 当父组件需要时也触发事件
        this.$emit('map-click', latlng);
        
        console.log('地图点击事件处理完成:', [latlng.lat, latlng.lng], '当前类型:', this.currentGeometryType);
      } catch (error) {
        console.error('处理地图点击事件失败:', error);
      }
    },
    
    // 鼠标移动事件
    onMouseMove(latlng) {
      try {
        const coords = mapUtils.extractCoordinatesFromMapEvent(latlng);
        if (coords) {
          this.mouseLat = coords.lat;
          this.mouseLng = coords.lng;
          // 如果处于绘制模式且已有点，则更新当前鼠标位置
          if (this.drawingMode && this.drawingCoordinates.length > 0 && !this.drawingCompleted) {
            this.currentMousePosition = [coords.lat, coords.lng];
          }
          this.$emit('mouse-move', coords);
        }
      } catch (error) {
        // 鼠标移动错误不输出，避免控制台刷屏
        if (!window._mouseMoveThrittled) {
          window._mouseMoveThrittled = true;
          console.warn('处理鼠标移动事件失败');
          
          // 节流错误输出，每3秒允许输出一次
          setTimeout(() => {
            window._mouseMoveThrittled = false;
          }, 3000);
        }
      }
    },
    
    // 更新中心点
    centerUpdated(center) {
      // 确保center是数组形式，如果是对象形式则转换为数组
      if (center && typeof center === 'object' && 'lat' in center && 'lng' in center) {
        this.center = [center.lat, center.lng];
      } else {
        this.center = center;
      }
      
      // 同步到状态存储
      bridgeMapStore.actions.setCenter(this.center);
      
      this.$emit('update:center', this.center);
    },
    
    // 更新缩放级别
    zoomUpdated(zoom) {
      this.zoom = zoom;
      
      // 同步到状态存储
      bridgeMapStore.actions.setZoom(zoom);
      
      this.$emit('update:zoom', zoom);
    },
    
    // 显示桥梁信息
    showBridgeInfo(bridge) {
      this.$emit('show-bridge-info', bridge);
    },
    
    // 设置地图中心
    setMapView(center, zoom) {
      if (this.map) {
        this.map.setView(center, zoom);
      }
    },
    
    // 解析区域坐标
    parseAreaCoordinates(area) {
      return mapUtils.parseAreaCoordinates(area);
    },
    
    // 获取区域颜色
    getAreaColor(areaType) {
      return this.getFixedColor();
    },
    
    // 获取区域类型名称
    getAreaTypeName(areaType) {
      if (!areaType) return '未知类型';
      return this.areaTypeNameMap[areaType] || areaType;
    },
    
    // 格式化经度
    formatLongitude(longitude) {
      return formatLongitude(longitude);
    },
    
    // 格式化纬度
    formatLatitude(latitude) {
      return formatLatitude(latitude);
    },
    
    // 处理区域点击事件
    handleAreaClick(area, event, fromButton = false) {
      // 如果当前处于绘制模式，则不处理点击事件
      if ((this.drawingMode || this.drawingCompleted) && !fromButton) {
        console.log('当前处于绘制或编辑模式，区域点击事件被忽略');
        return;
      }
      
      // 阻止事件冒泡，避免触发地图的点击事件
      if (event) {
        event.originalEvent && event.originalEvent.stopPropagation();
      }
      
      this.currentArea = area;
      console.log('区域点击事件：', area.id, area.areaName || area.name);
      
      // 同步到状态存储，使用skipEvent=true避免触发事件循环
      bridgeMapStore.actions.setCurrentArea(area, fromButton);
      
      // 先触发选中事件，再触发编辑事件（两步操作）
      this.$emit('edit-area', area);
      
      // 给UI一些时间更新选中状态后再进入编辑模式
      setTimeout(() => {
        this.$emit('edit-current-area', area);
      }, 100);
    },
    
    // 撤销最后一个绘制点
    undoLastPoint() {
      // 如果是已完成绘制状态，不允许撤销
      if (this.drawingCompleted) return;
      
      if (this.drawingCoordinates.length > 0) {
        // 创建新的坐标数组，而不是直接修改props
        const newCoordinates = [...this.drawingCoordinates];
        // 保存当前点到历史记录
        const lastPoint = newCoordinates.pop();
        this.drawingHistory.push(lastPoint);
        
        // 更新绘制坐标并通知父组件
        this.$emit('update:drawing-coordinates', newCoordinates);
      }
    },
    
    // 重做(恢复)最后一个撤销的点
    redoLastPoint() {
      // 如果是已完成绘制状态，不允许重做
      if (this.drawingCompleted) return;
      
      if (this.drawingHistory.length > 0) {
        // 从历史记录恢复点
        const pointToRestore = this.drawingHistory.pop();
        const updatedCoordinates = [...this.drawingCoordinates, pointToRestore];
        
        // 更新绘制坐标并通知父组件
        this.$emit('update:drawing-coordinates', updatedCoordinates);
      }
    },
    
    // 清除绘制
    clearDrawing() {
      // 【修复循环调用】：去掉事件发出，避免与BridgeManagement.js形成循环调用
      // this.$emit('clear-drawing'); // 已被直接调用，不需要再发出事件
      console.log('BridgeMap: 开始清除绘制内容');
      
      // 清空点标注列表
      if (this.pointMarkers.length > 0) {
        console.log('清除点标注，数量:', this.pointMarkers.length);
        this.pointMarkers = [];
      }
      
      // 【补充清理】：清空绘制坐标
      if (this.drawingCoordinates && this.drawingCoordinates.length > 0) {
        console.log('清除绘制坐标，数量:', this.drawingCoordinates.length);
        // 通知父组件清空坐标
        this.$emit('update:drawing-coordinates', []);
      }
      
      // 清空历史记录
      this.drawingHistory = [];
      // 清除当前鼠标位置
      this.currentMousePosition = null;
      // 重置填充多边形状态
      this.shouldShowFilledPolygon = false;
      // 重置绘制完成状态
      this.drawingCompleted = false;
      // 重置拖拽状态
      this.isPointDragging = false;
      this.draggingPointIndex = null;
      this.selectedPointIndex = null;
      
      // 隐藏右键菜单
      this.hideContextMenu();
      
      console.log('BridgeMap: 绘制内容清除完成');
    },
    
    // 完成绘制
    finishDrawing() {
      // 🎯 根据不同类型检查完成条件
      let canFinish = false;
      let message = '';
      
      switch (this.currentGeometryType) {
        case 'LINESTRING':
          canFinish = this.drawingCoordinates.length >= 2;
          message = canFinish ? '线段绘制已完成' : '线段需要至少2个点';
          break;
        case 'RECTANGLE':
          canFinish = this.drawingCoordinates.length >= 4;
          message = canFinish ? '矩形绘制已完成' : '矩形需要4个点';
          break;
        case 'CIRCLE':
          canFinish = this.drawingCoordinates.length >= 2;
          message = canFinish ? '圆形绘制已完成，现在您可以拖拽中心点或边界点调整' : '圆形需要至少2个点';
          break;
        default: // POLYGON
          canFinish = this.drawingCoordinates.length >= 3;
          message = canFinish ? '多边形绘制已完成，现在您可以拖拽或右键删除各个点' : '多边形需要至少3个点';
          break;
      }
      
      if (canFinish) {
        // 设置填充多边形效果（线段不需要填充）
        this.shouldShowFilledPolygon = this.currentGeometryType !== 'LINESTRING';
        // 设置绘制完成状态，允许编辑点
        this.drawingCompleted = true;
        // 通知父组件绘制模式关闭
        this.$emit('update:drawing-mode', false);
        
        // 显示提示
        this.$message({
          type: 'success',
          message: message
        });
        
        // 通知父组件绘制完成
        this.$emit('finish-drawing');
      } else {
        // 显示错误提示
        this.$message.warning(message);
      }
      
      // 清除当前鼠标位置
      this.currentMousePosition = null;
      
      // 隐藏右键菜单
      this.hideContextMenu();
    },
    
    // 进入点编辑模式
    enterPointEditMode(coordinates) {
      // 设置坐标
      this.$emit('update:drawing-coordinates', coordinates);
      
      // 设置状态
      this.shouldShowFilledPolygon = true;
      this.drawingCompleted = true;
      // 通知父组件关闭绘制模式
      this.$emit('update:drawing-mode', false);
      
      // // 显示提示
      // this.$message({
      //   type: 'info',
      //   message: '您可以拖拽点调整位置，右键点可以删除'
      // });
    },

    // 处理区域可见性变化事件
    handleAreaVisibilityChanged(area) {
      if (!area) return;
      
      console.log('BridgeMap: 接收到区域可见性变化事件，区域ID:', area.id, '是否可见:', area.visible !== false);
      
      if (this.map) {
        // 强制刷新地图视图
        this.map.invalidateSize();
        
        // 由于我们在BridgeMap.vue中使用了v-if条件判断，只要传入的savedAreas更新了，
        // Vue会自动处理区域的隐藏/显示，不需要手动操作地图图层
      }
    },

    // 处理区域颜色变化事件
    handleAreaColorChanged(data) {
      if (!data) return;
      
      console.log('BridgeMap: 接收到区域颜色变化事件，新颜色:', data.color);
      
      // 更新当前绘制颜色
      this.currentDrawingColor = data.color;
      
      // 如果存在点标注，同时更新点标注的颜色 - 不再检查区域类型
      if (this.pointMarkers.length > 0) {
        // 更新第一个点标注的颜色
        this.pointMarkers[0].color = data.color;
        console.log('已更新正在绘制的点标注颜色:', data.color);
      }
      
      // 【关键修复】更新已保存的点标注颜色
      if (data.areaId && this.savedAreas && this.savedAreas.length > 0) {
        const targetArea = this.savedAreas.find(area => area.id === data.areaId);
        if (targetArea && targetArea.geometryType === 'POINT') {
          targetArea.color = data.color;
          console.log('已更新savedAreas中点标注的颜色:', data.color, '区域ID:', data.areaId);
          
          // 强制更新计算属性以重新渲染
          this.mapUpdateCounter++;
          this.$forceUpdate();
        }
      }
      
      // 同时更新store中的当前区域颜色
      const currentArea = bridgeMapStore.state.currentArea;
      if (currentArea && currentArea.geometryType === 'POINT') {
        currentArea.color = data.color;
        console.log('已更新store中当前区域的颜色:', data.color);
      }
      
      // 强制刷新地图视图以更新颜色
      if (this.map) {
        this.$nextTick(() => {
          this.map.invalidateSize();
        });
      }
    },
    
    // 设置点标注图标
    setPointIcon(iconName) {
      if (iconName) {
        this.currentPointIcon = iconName;
      }
    },
    
    // 添加点标注
    addPointMarker(options) {
      try {
        if (!options || !options.coordinates || options.coordinates.length < 2) {
          console.warn('添加点标注失败：无效的坐标', options);
          return;
        }
        
        // 验证坐标数据类型
        const lat = Number(options.coordinates[0]);
        const lng = Number(options.coordinates[1]);
        if (isNaN(lat) || isNaN(lng)) {
          console.warn('添加点标注失败：坐标不是有效数字', options.coordinates);
          return;
        }
        
        // 创建点标注数据
        const marker = {
          id: `point-marker-${Date.now()}`,
          position: [lat, lng], // 确保坐标是数字格式
          iconName: options.iconName || this.currentPointIcon || 'el-icon-location',
          label: options.name || '',
          color: options.color || this.getFixedColor()
        };
        
        // 清空现有点标注（确保只有一个点标注）
        this.pointMarkers = [];
        
        // 添加到点标注列表
        this.pointMarkers.push(marker);
        
        // 同步更新绘制坐标，确保拖拽时能更新
        if (this.drawingCoordinates.length === 0) {
          this.$emit('update:drawing-coordinates', [[lat, lng]]);
        } else {
          // 更新现有点坐标
          this.drawingCoordinates[0] = [lat, lng];
        }
        
        // 设置绘制完成状态，使点可拖拽
        this.drawingCompleted = true;
        
        // 通知父组件更新当前区域类型，将点标注的区域类型设置为'OTHER'而不是'POINT'
        // POINT是几何类型(geometryType)而非区域类型(areaType)
        this.$emit('update:current-area-type', 'OTHER');
        
        // 更新地图视图
        this.$nextTick(() => {
          if (this.map) {
            this.map.invalidateSize();
          }
        });
        
        console.log('点标注已添加，坐标:', [lat, lng], '图标:', marker.iconName, '标签:', marker.label);
        return marker;
      } catch (error) {
        console.error('添加点标注出错:', error);
        return null;
      }
    },
    
    // 更新点标注
    updatePointMarker(markerId, options) {
      try {
        // 查找要更新的点标注
        const markerIndex = this.pointMarkers.findIndex(m => m.id === markerId);
        if (markerIndex === -1) {
          console.warn(`找不到ID为${markerId}的点标注`);
          return false;
        }
        
        // 更新点标注属性
        const marker = this.pointMarkers[markerIndex];
        if (options.coordinates) marker.position = options.coordinates;
        if (options.iconName) marker.iconName = options.iconName;
        if (options.name) marker.label = options.name;
        if (options.color) marker.color = options.color;
        
        // 触发更新
        this.$nextTick(() => {
          if (this.map) {
            this.map.invalidateSize();
          }
        });
        
        return true;
      } catch (error) {
        console.error('更新点标注出错:', error);
        return false;
      }
    },
    
    // 删除点标注
    removePointMarker(markerId) {
      try {
        // 查找要删除的点标注
        const markerIndex = this.pointMarkers.findIndex(m => m.id === markerId);
        if (markerIndex === -1) {
          console.warn(`找不到ID为${markerId}的点标注`);
          return false;
        }
        
        // 移除点标注
        this.pointMarkers.splice(markerIndex, 1);
        
        // 更新地图视图
        this.$nextTick(() => {
          if (this.map) {
            this.map.invalidateSize();
          }
        });
        
        return true;
      } catch (error) {
        console.error('删除点标注出错:', error);
        return false;
      }
    },

    // 更新点标注图标
    updatePointMarkerIcon(iconName) {
      if (!iconName) return false;
      
      console.log('BridgeMap: 开始更新点标注图标:', iconName);
      let updated = false;
      
      try {
        // 1. 更新正在绘制的点标注（pointMarkers数组）
        if (this.pointMarkers.length > 0) {
          this.pointMarkers.forEach((marker, index) => {
          marker.iconName = iconName;
            console.log(`已更新pointMarkers[${index}]的图标:`, iconName);
          });
          updated = true;
        }
        
        // 2. 更新当前选中的区域（bridgeMapStore中的currentArea）
        const currentArea = bridgeMapStore.state.currentArea;
        if (currentArea && currentArea.geometryType === 'POINT') {
          currentArea.icon = iconName;
          currentArea.iconName = iconName;
          console.log('已更新store中currentArea的图标:', iconName);
          updated = true;
        }
        
        // 3. 更新savedAreas中的点标注（针对已保存的点标注）
        if (this.savedAreas && this.savedAreas.length > 0) {
          this.savedAreas.forEach((area, index) => {
            if (area.geometryType === 'POINT') {
              // 如果是当前正在编辑的区域，或者是所有点标注都需要更新
              const isCurrentEditingArea = currentArea && currentArea.id === area.id;
              if (isCurrentEditingArea) {
                area.icon = iconName;
                area.iconName = iconName;
                console.log(`已更新savedAreas[${index}]的图标:`, iconName);
                updated = true;
              }
            }
          });
        }
        
        // 4. 如果有任何更新，强制重新计算和渲染
        if (updated) {
          // 增加计数器，触发pointMarkerIcons计算属性重新计算
          this.mapUpdateCounter++;
          
          // 强制视图更新
          this.$forceUpdate();
          
          // 如果地图存在，也刷新地图
          if (this.map) {
            this.$nextTick(() => {
              this.map.invalidateSize();
            });
          }
          
          console.log('BridgeMap: 点标注图标已成功更新:', iconName);
        } else {
          console.warn('BridgeMap: 没有找到需要更新的点标注');
        }
        
        return updated;
      } catch (error) {
        console.error('更新点标注图标出错:', error);
        return false;
      }
    },

    // 更新点标注颜色
    updatePointMarkerColor(color) {
      if (!color) return;
      
      try {
        // 更新正在绘制的点标注颜色
        if (this.pointMarkers.length > 0) {
          const marker = this.pointMarkers[0];
          marker.color = color;
          
          // 强制视图更新
          this.$forceUpdate();
          
          console.log('点标注颜色已直接更新:', color);
          return true;
        }
        
        // 同时检查已保存的点标注
        const currentArea = bridgeMapStore.state.currentArea;
        if (currentArea && currentArea.geometryType === 'POINT') {
          currentArea.color = color;
          
          // 强制更新计算属性
          this.mapUpdateCounter++;
          this.$forceUpdate();
          
          console.log('已保存点标注颜色已更新:', color);
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('更新点标注颜色出错:', error);
        return false;
      }
    },
    
    // 更新点标注名称/标签
    updatePointMarkerLabel(label) {
      if (!label) return;
      
      try {
        // 更新正在绘制的点标注标签
        if (this.pointMarkers.length > 0) {
          const marker = this.pointMarkers[0];
          marker.label = label;
          
          // 强制视图更新
          this.$forceUpdate();
          
          console.log('点标注标签已直接更新:', label);
          return true;
        }
        
        // 同时检查已保存的点标注
        const currentArea = bridgeMapStore.state.currentArea;
        if (currentArea && currentArea.geometryType === 'POINT') {
          currentArea.name = label;
          currentArea.areaName = label;
          
          // 强制更新计算属性
          this.mapUpdateCounter++;
          this.$forceUpdate();
          
          console.log('已保存点标注标签已更新:', label);
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('更新点标注标签出错:', error);
        return false;
      }
    },

    // 判断指定索引的点是否被选中（正在拖拽或鼠标按下）
    isPointSelected(index) {
      return (this.isPointDragging && this.draggingPointIndex === index) || 
             (this.isMouseDown && this.selectedPointIndex === index);
    },
    
    // 处理点标注标记的鼠标按下事件
    handlePointMarkerMouseDown(marker, _event) {
      // 修改判断条件，允许在区域类型为'OTHER'且有点标注时也能拖动
      // 判断当前是否为点标注 - 通过检查几何类型和pointMarkers而非区域类型
      if (!this.drawingCompleted || this.pointMarkers.length === 0) {
        return;
      }
      
      // 记录拖拽状态
      this.isMouseDown = true;
      this.isPointDragging = true;
      this.selectedPointIndex = 0;
      this.draggingPointIndex = 0;
      
      // 禁用地图拖拽功能
      if (this.map && this.map.dragging) {
        this.map.dragging.disable();
        this.mapDraggingDisabled = true;
      }
      
      console.log('点标注鼠标按下:', marker.id);
    },
    
    // 处理点标注标记的拖拽结束事件
    handlePointMarkerDragEnd(marker, _event) {
      // 更新点标注位置
      if (marker && marker.position) {
        // 同步更新绘制坐标
        if (this.drawingCoordinates.length === 0) {
          this.$emit('update:drawing-coordinates', [marker.position]);
        } else {
          // 更新现有点坐标
          const updatedCoordinates = [...this.drawingCoordinates];
          updatedCoordinates[0] = marker.position;
          this.$emit('update:drawing-coordinates', updatedCoordinates);
        }
        
        // 触发位置更新事件
        this.$emit('point-marker-moved', {
          markerId: marker.id,
          position: marker.position
        });
        
        console.log('点标注拖拽结束:', marker.position);
      }
      
      // 重置拖拽状态
      this.resetDragState();
    },

    // 🎯 处理地图容器的拖拽悬停事件 - 解决HTML5拖拽光标问题
    handleMapDragOver(event) {
      // 阻止默认行为，允许放置
      event.preventDefault();
      // 设置拖拽效果为移动
      event.dataTransfer.dropEffect = 'move';
    },

    // 🎯 处理地图容器的放置事件 - 解决HTML5拖拽光标问题
    handleMapDrop(event) {
      // 阻止默认行为
      event.preventDefault();
      // 不需要处理具体的放置逻辑，因为我们在其他地方处理坐标更新
      console.log('地图容器接收到放置事件');
    },
    
    // 🎯 计算两点间的直线距离（公里）
    calculateDistanceKm(point1, point2) {
      return this.calculateDistanceInMeters(point1, point2) / 1000;
    },
    
    // 🎯 计算方向角（正北为0度，顺时针0-360度）
    calculateBearing(point1, point2) {
      const lat1 = point1[0] * Math.PI / 180;
      const lat2 = point2[0] * Math.PI / 180;
      const deltaLng = (point2[1] - point1[1]) * Math.PI / 180;
      
      const x = Math.sin(deltaLng) * Math.cos(lat2);
      const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
      
      let bearing = Math.atan2(x, y) * 180 / Math.PI;
      // 转换为0-360度
      bearing = (bearing + 360) % 360;
      
      return bearing;
    },
    

    

    
    // 🎯 处理工具切换事件
    handleToolChange(tool) {
      console.log('工具切换:', tool);
      
      if (tool === 'measure') {
        // 🎯 如果已经在测距模式，则退出并清理所有线段
        if (this.isMeasureMode) {
          this.exitMeasureModeAndClearAll();
        } else {
          this.enableMeasureMode();
        }
      } else {
        // 🎯 当tool为空字符串且当前在测距模式时，也应该退出并清理
        if (tool === '' && this.isMeasureMode) {
          this.exitMeasureModeAndClearAll();
        } else {
          this.disableMeasureMode();
        }
      }
    },
    
    // 🎯 启用测距模式
    enableMeasureMode() {
      this.isMeasureMode = true;
      this.measureDrawing = false;
      this.measureCoordinates = [];
      this.measureMousePosition = null;
      console.log('测距模式已启用, isMeasureMode:', this.isMeasureMode);
      console.log('当前measureLines数量:', this.measureLines.length);
      this.$forceUpdate(); // 强制更新视图
      this.$message.info('测距模式已启用，请点击地图上的两个点进行测距');
    },
    
    // 🎯 禁用测距模式
    disableMeasureMode() {
      this.isMeasureMode = false;
      this.measureDrawing = false;
      this.measureCoordinates = [];
      this.measureMousePosition = null;
      
      // 清理Leaflet对象
      if (this.measureStartMarker) {
        this.map.removeLayer(this.measureStartMarker);
        this.measureStartMarker = null;
      }
      if (this.measurePreviewPolyline) {
        this.map.removeLayer(this.measurePreviewPolyline);
        this.measurePreviewPolyline = null;
      }
      if (this.measurePreviewTooltip) {
        this.map.removeLayer(this.measurePreviewTooltip);
        this.measurePreviewTooltip = null;
      }
      
      console.log('测距模式已禁用');
    },
    
    // 🎯 退出测距模式并清理所有线段
    exitMeasureModeAndClearAll() {
      // 先禁用测距模式
      this.disableMeasureMode();
      
      // 清理所有测距线段
      this.clearAllMeasureLines();
      
      this.$message.info('已退出测距模式并清理所有线段');
      console.log('退出测距模式并清理所有线段完成');
    },
    
    // 🎯 清理所有测距线段
    clearAllMeasureLines() {
      // 删除所有测距线段的Leaflet对象
      this.measureLines.forEach(line => {
        if (line.leafletPolyline && this.map) {
          this.map.removeLayer(line.leafletPolyline);
        }
        if (line.leafletMarker && this.map) {
          this.map.removeLayer(line.leafletMarker);
        }
        if (line.leafletDeleteMarker && this.map) {
          this.map.removeLayer(line.leafletDeleteMarker);
        }
      });
      
      // 清空测距线段数组
      this.measureLines = [];
      
      console.log('所有测距线段已清理');
    },
    
    // 🎯 处理测距点击
    handleMeasureClick(latlng) {
      // 确保坐标是纯JavaScript数组，不是Vue的Observer对象
      const point = [parseFloat(latlng.lat), parseFloat(latlng.lng)];
      
      if (!this.measureDrawing) {
        // 开始新的测距线段
        this.measureDrawing = true;
        this.measureCoordinates = [point];
        
        // 🎯 直接使用Leaflet API添加起点标记
        if (this.map) {
          this.measureStartMarker = this.L.marker(point, {
            icon: this.getMeasureStartIcon()
          }).addTo(this.map);
          console.log('已通过Leaflet API添加起点标记');
        }
        
        console.log('测距起点已设置:', point);
        console.log('measureDrawing状态:', this.measureDrawing);
        console.log('measureCoordinates:', this.measureCoordinates);
        this.$message.info('已设置起点，请点击终点完成测距');
      } else if (this.measureCoordinates.length === 1) {
        // 完成测距线段
        this.measureCoordinates.push(point);
        
        // 计算距离和方向角
        const distance = this.calculateDistanceInMeters(this.measureCoordinates[0], this.measureCoordinates[1]);
        const bearing = this.calculateBearing(this.measureCoordinates[0], this.measureCoordinates[1]);
        
        // 创建测距线段数据，确保坐标是纯数组
        const measureLine = {
          id: Date.now(),
          points: JSON.parse(JSON.stringify(this.measureCoordinates)), // 确保是纯数组
          distance: distance,
          bearing: bearing,
          distanceText: this.formatDistance(distance),
          bearingText: this.formatBearing(bearing)
        };
        
        // 保存测距线段
        this.measureLines.push(measureLine);
        
        // 🎯 直接使用Leaflet API添加线段（避免vue2-leaflet问题）
        if (this.map) {
          const polyline = this.L.polyline(measureLine.points, {
            color: '#ff4444',
            weight: 3,
            opacity: 0.8
          }).addTo(this.map);
          
          // 添加点击事件支持删除（直接删除，不弹对话框）
          polyline.on('click', () => {
            this.deleteMeasureLineAndRestart(measureLine.id);
          });
          
          // 添加标签
          const midpoint = this.getMeasureLineMidpoint(measureLine.points);
          const marker = this.L.marker(midpoint, {
            icon: this.getMeasureLineIcon(measureLine)
          }).addTo(this.map);
          
          // 在第二个点位置添加删除标记
          console.log('删除标记位置:', measureLine.points[1]);
          const deleteMarker = this.L.marker(measureLine.points[1], {
            icon: this.getMeasureDeleteIcon(measureLine.id)
          }).addTo(this.map);
          console.log('删除标记已添加到地图:', deleteMarker);
          
          // 添加删除标记的点击事件
          deleteMarker.on('click', (event) => {
            // 阻止事件冒泡，防止触发线段的点击事件
            if (event.originalEvent) {
              event.originalEvent.stopPropagation();
            }
            this.deleteMeasureLineAndRestart(measureLine.id);
          });
          
          // 保存引用以便删除
          measureLine.leafletPolyline = polyline;
          measureLine.leafletMarker = marker;
          measureLine.leafletDeleteMarker = deleteMarker;
          
          console.log('已通过Leaflet API添加测距线段到地图');
        }
        
        // 清理起点标记、预览线段和预览提示
        if (this.measureStartMarker) {
          this.map.removeLayer(this.measureStartMarker);
          this.measureStartMarker = null;
        }
        if (this.measurePreviewPolyline) {
          this.map.removeLayer(this.measurePreviewPolyline);
          this.measurePreviewPolyline = null;
        }
        if (this.measurePreviewTooltip) {
          this.map.removeLayer(this.measurePreviewTooltip);
          this.measurePreviewTooltip = null;
        }
        
        // 重置绘制状态
        this.measureDrawing = false;
        this.measureCoordinates = [];
        this.measureMousePosition = null;
        
        console.log('测距完成:', measureLine);
        console.log('当前measureLines数量:', this.measureLines.length);
        console.log('isMeasureMode状态:', this.isMeasureMode);
        
        this.$message.success(`测距完成: ${measureLine.distanceText}, ${measureLine.bearingText}`);
      }
    },
    
    // 🎯 格式化距离显示
    formatDistance(distanceInMeters) {
      if (distanceInMeters < 1000) {
        return `${Math.round(distanceInMeters)}m`;
      } else {
        return `${(distanceInMeters / 1000).toFixed(2)}km`;
      }
    },
    
    // 🎯 格式化方向角显示
    formatBearing(bearing) {
      return `${Math.round(bearing)}°`;
    },
    
    // 🎯 获取测距线段中点坐标
    getMeasureLineMidpoint(points) {
      if (!points || points.length < 2) return [0, 0];
      const lat = (points[0][0] + points[1][0]) / 2;
      const lng = (points[0][1] + points[1][1]) / 2;
      return [lat, lng];
    },
    
    // 🎯 获取测距删除标记位置（在文本标签上方）
    getMeasureDeletePosition(points) {
      const midpoint = this.getMeasureLineMidpoint(points);
      // 在纬度上向北偏移约0.0002度（约22米），确保删除按钮在文本上方
      const deletePosition = [midpoint[0] + 0.0002, midpoint[1]];
      return deletePosition;
    },
    
    // 🎯 获取测距起点标记图标
    getMeasureStartIcon() {
      return this.L.divIcon({
        html: '<div class="measure-start-marker"></div>',
        className: 'measure-start-point',
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
    },
    
    // 🎯 获取测距预览提示图标
    getMeasurePreviewTooltipIcon(distance, bearing) {
      const distanceText = this.formatDistance(distance);
      const bearingText = this.formatBearing(bearing);
      
      return this.L.divIcon({
        html: `
          <div class="measure-preview-tooltip">
            <div class="measure-preview-distance">${distanceText}</div>
            <div class="measure-preview-bearing">${bearingText}</div>
          </div>
        `,
        className: 'measure-preview-tooltip-container',
        iconSize: [80, 40],
        iconAnchor: [40, 50] // 显示在鼠标位置下方
      });
    },
    
    // 🎯 获取测距删除图标
    getMeasureDeleteIcon(lineId) {
      console.log('called getMeasureDeleteIcon for line:', lineId)
      return this.L.divIcon({
        html: '<div class="measure-delete-marker"><i class="el-icon-close"></i></div>',
        className: `measure-delete-container-${lineId}`, // 每个删除标记使用唯一className
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
    },
    
    // 🎯 获取测距线段标签图标
    getMeasureLineIcon(line) {
      // 计算文本旋转角度（使文本与线段平行）
      const lineAngle = this.calculateLineAngle(line.points[0], line.points[1]);
      
      return this.L.divIcon({
        html: `
          <div class="measure-label" style="transform: rotate(${lineAngle}deg);">
            <div class="measure-distance">${line.distanceText}</div>
            <div class="measure-bearing">${line.bearingText}</div>
          </div>
        `,
        className: `custom-measure-icon-${line.id}`, // 每个标签使用唯一className
        iconSize: [80, 40],
        iconAnchor: [40, 20]
      });
    },
    
    // 🎯 计算线段角度（用于文本旋转）
    calculateLineAngle(point1, point2) {
      // point1和point2格式: [lat, lng]
      // 在屏幕坐标系中，经度差作为x轴，纬度差作为y轴
      const deltaX = point2[1] - point1[1]; // 经度差
      const deltaY = point1[0] - point2[0]; // 纬度差（注意Y轴反向）
      let angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
      
      // 确保文本不会倒置（角度在-90到90度之间）
      if (angle > 90) {
        angle -= 180;
      } else if (angle < -90) {
        angle += 180;
      }
      
      return angle;
    },
    
    // 🎯 删除测距线段
    deleteMeasureLine(lineId) {
      const index = this.measureLines.findIndex(line => line.id === lineId);
      if (index !== -1) {
        const line = this.measureLines[index];
        
        // 从地图上移除Leaflet对象
        if (line.leafletPolyline && this.map) {
          this.map.removeLayer(line.leafletPolyline);
        }
        if (line.leafletMarker && this.map) {
          this.map.removeLayer(line.leafletMarker);
        }
        if (line.leafletDeleteMarker && this.map) {
          this.map.removeLayer(line.leafletDeleteMarker);
        }
        
        this.measureLines.splice(index, 1);
        this.$message.success('测距线段已删除');
        console.log('已删除测距线段:', lineId);
      }
    },
    
    // 🎯 删除测距线段并重新开始绘制
    deleteMeasureLineAndRestart(lineId) {
      // 删除指定的测距线段
      this.deleteMeasureLine(lineId);
      
      // 🎯 完全重置测距状态，防止出现意外预览
      this.measureDrawing = false;
      this.measureCoordinates = [];
      this.measureMousePosition = null;
      
      // 清理当前的预览对象
      if (this.measureStartMarker && this.map) {
        this.map.removeLayer(this.measureStartMarker);
        this.measureStartMarker = null;
      }
      if (this.measurePreviewPolyline && this.map) {
        this.map.removeLayer(this.measurePreviewPolyline);
        this.measurePreviewPolyline = null;
      }
      if (this.measurePreviewTooltip && this.map) {
        this.map.removeLayer(this.measurePreviewTooltip);
        this.measurePreviewTooltip = null;
      }
      
      // 🎯 强制更新UI，确保状态同步
      this.$nextTick(() => {
        this.$forceUpdate();
      });
      
      this.$message.info('已删除线段，可重新开始测距');
      console.log('已删除测距线段并完全重置状态，measureDrawing:', this.measureDrawing, 'measureCoordinates:', this.measureCoordinates);
    },
    

    
    // 🎯 处理定位事件
    handleGotoLocation(location) {
      if (this.map && location.lat && location.lng) {
        this.map.setView([location.lat, location.lng], 15);
        console.log('地图已定位到:', location);
      }
    },
    
    // 🎯 处理缩放事件
    handleZoom(delta) {
      if (this.map) {
        const currentZoom = this.map.getZoom();
        this.map.setZoom(currentZoom + delta);
      }
    },
    
    // 🎯 处理重置视图事件
    handleResetView() {
      if (this.map) {
        this.map.setView(this.center, this.zoom);
        this.$message.info('地图视图已重置');
      }
    },
    
    // 🎯 处理全屏切换事件
    handleToggleFullscreen() {
      // 这里可以实现全屏逻辑
      this.$message.info('全屏功能待实现');
    },
    
    // 🎯 处理图层可见性变化事件
    handleLayerVisibilityChange(data) {
      console.log('图层可见性变化:', data);
      // 这里可以实现图层控制逻辑
    }
  },
  watch: {
    // 🎯 监听几何类型变化
    currentGeometryTypeProp(newVal, oldVal) {
      if (newVal !== oldVal) {
        console.log(`BridgeMap: 几何类型已变更从 ${oldVal} 到 ${newVal}`);
        console.log(`BridgeMap: 当前计算得到的几何类型: ${this.currentGeometryType}`);
        console.log(`BridgeMap: store 中的 markType: ${bridgeMapStore.state.markType}`);
      }
    },
    
    // 监听绘制模式变化
    drawingMode(newVal) {
      if (!newVal) {
        // 如果绘制模式关闭，清除当前鼠标位置
        this.currentMousePosition = null;
        // 隐藏右键菜单
        this.hideContextMenu();
      }
    },
    // 监听绘制坐标变化，如果清空了坐标，也清除鼠标位置
    drawingCoordinates(newVal) {
      if (!newVal || newVal.length === 0) {
        this.currentMousePosition = null;
        this.shouldShowFilledPolygon = false;
        this.drawingCompleted = false;
      }
      
      // 强制更新点位置
      this.$nextTick(() => {
        this.updatePointsPosition();
      });
    },
    // 监听地图缩放或平移，更新点的位置
    zoom() {
      this.$nextTick(() => {
        this.updatePointsPosition();
      });
    },
    center() {
      this.$nextTick(() => {
        this.updatePointsPosition();
      });
    },
    // 监听区域数据变化，重新执行标注防重叠布局
    savedAreas() {
      this.$nextTick(() => {
        if (this.mapLabelManager && this.map) {
          this.mapLabelManager.scheduleLayout();
        }
      });
    }
  }
};